import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { fabric } from 'fabric';
import '@fortawesome/fontawesome-free/css/all.min.css';
// @ts-ignore - imagetracerjs doesn't have types
import ImageTracer from 'imagetracerjs';
import html2canvas from 'html2canvas';
import { MagicLogoModalProps, Technique, LogoData } from '../types';
import '../styles/MagicLogoModal.css';
import LogoWizard from './LogoWizard';

// Generate or retrieve client fingerprint for session identification
const getClientFingerprint = (): string => {
  const storageKey = 'magic_logo_fingerprint';
  let fingerprint = localStorage.getItem(storageKey);
  if (!fingerprint) {
    // Generate a unique fingerprint using timestamp + random string
    fingerprint = `fp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(storageKey, fingerprint);
  }
  return fingerprint;
};

// Translations - German (de) is default, Dutch (nl), English (en)
type Language = 'de' | 'nl' | 'en';
type TranslationKey =
  | 'upload' | 'recent' | 'removeBg' | 'vectorize' | 'crop' | 'clear' | 'undo' | 'save'
  | 'technique' | 'colors' | 'full' | 'back' | 'select' | 'move' | 'delete' | 'zoomIn' | 'zoomOut' | 'resetView'
  | 'applyCrop' | 'cancelCrop' | 'magic3d' | 'magicLogo' | 'selectTechnique' | 'selectColors'
  | 'processing' | 'removingBg' | 'vectorizing' | 'noTechniques' | 'dropFilesHere' | 'orClickToUpload'
  | 'backTo2D' | 'openFullScreen' | 'magic3dPreview'
  | 'clickOrDragToUpload' | 'configureProduct' | 'selectPrintTechnique' | 'numberOfColors'
  | 'colorVariants' | 'oneColor' | 'maxColors' | 'noImageFound' | 'engraving'
  | 'removeBgError' | 'xColors' | 'fullColor'
  | 'noImageForRemoveBg' | 'noImageForVectorize' | 'vectorizeError' | 'noImageForCrop' | 'cropOutsideImage'
  | 'productPreview' | 'basicMode' | 'advancedMode' | 'logoSize'
  | 'copyLogo' | 'copySvg' | 'copied' | 'copyError' | 'noLogoToCopy'
  | 'copy3D' | 'copying3D' | 'no3DModel'
  | 'downloadPdf' | 'generatingPdf'
  | 'addToOrder' | 'addingToOrder' | 'addedToOrder' | 'addToOrderError' | 'noLogoForOrder'
  | 'aiRender' | 'aiRendering' | 'aiRenderReady' | 'aiRenderGenerate' | 'aiRenderSuccess'
  | 'aiRenderError' | 'aiRenderCopy' | 'aiRenderDownload' | 'aiRenderRetry' | 'aiRenderClose'
  | 'aiRenderPromptLabel' | 'aiRenderTakingLonger';

const translations: Record<Language, Record<TranslationKey, string>> = {
  de: {
    upload: 'Hochladen',
    recent: 'Neueste',
    removeBg: 'Hintergrund entf.',
    vectorize: 'Vektorisieren',
    crop: 'Zuschneiden',
    clear: 'Leeren',
    undo: 'Ruckgangig',
    save: 'Speichern',
    technique: 'Technik',
    colors: 'Farben',
    full: 'Voll',
    back: 'Zuruck',
    select: 'Auswahlen',
    move: 'Verschieben',
    delete: 'Loschen',
    zoomIn: 'Vergroaern',
    zoomOut: 'Verkleinern',
    resetView: 'Ansicht zurücksetzen',
    applyCrop: 'Zuschneiden anwenden',
    cancelCrop: 'Abbrechen',
    magic3d: 'Magic 3D',
    magicLogo: 'Magic Logotool',
    selectTechnique: 'Technik wahlen',
    selectColors: 'Farben wahlen',
    processing: 'Verarbeitung...',
    removingBg: 'Entferne Hintergrund...',
    vectorizing: 'Vektorisiere...',
    noTechniques: 'Keine Techniken verfugbar',
    dropFilesHere: 'Dateien hier ablegen',
    orClickToUpload: 'oder klicken zum Hochladen',
    backTo2D: 'Zuruck zu 2D',
    openFullScreen: 'Vollbild offnen',
    magic3dPreview: 'Magic 3D Vorschau',
    clickOrDragToUpload: 'Klicken oder Logo hierher ziehen zum Hochladen',
    configureProduct: 'Produkt konfigurieren',
    selectPrintTechnique: 'Drucktechnik auswahlen',
    numberOfColors: 'Anzahl Farben',
    colorVariants: 'Farbvarianten',
    oneColor: '1 Farbe',
    maxColors: 'Max {count} Farben',
    noImageFound: 'Kein Bild gefunden',
    engraving: 'Gravur',
    removeBgError: 'Fehler beim Entfernen des Hintergrunds. API-Key uberprufen oder spater erneut versuchen.',
    xColors: '{count} Farben',
    fullColor: 'Vollfarbig',
    noImageForRemoveBg: 'Kein Bild zum Entfernen des Hintergrunds gefunden',
    noImageForVectorize: 'Kein Bild zum Vektorisieren gefunden',
    vectorizeError: 'Fehler beim Vektorisieren. Versuchen Sie ein anderes Bild.',
    noImageForCrop: 'Kein Bild zum Zuschneiden gefunden',
    cropOutsideImage: 'Zuschneideauswahl liegt ausserhalb des Bildes',
    productPreview: 'Produktvorschau',
    basicMode: 'Basic',
    advancedMode: 'Advanced',
    logoSize: 'Logo-Grosse',
    copyLogo: 'Artwork kopieren',
    copySvg: 'SVG kopieren',
    copied: 'Kopiert!',
    copyError: 'Kopieren fehlgeschlagen',
    noLogoToCopy: 'Kein Logo zum Kopieren vorhanden',
    copy3D: '3D kopieren',
    copying3D: '3D wird kopiert...',
    no3DModel: 'Kein 3D Modell verfugbar',
    downloadPdf: 'PDF',
    generatingPdf: 'PDF wird erstellt...',
    addToOrder: 'Zur Bestellung',
    addingToOrder: 'Wird hinzugefugt...',
    addedToOrder: 'Hinzugefugt!',
    addToOrderError: 'Fehler beim Hinzufugen zur Bestellung',
    noLogoForOrder: 'Bitte laden Sie zuerst ein Logo hoch',
    aiRender: 'AI Render',
    aiRendering: 'AI generiert...',
    aiRenderReady: 'Bereit zum Generieren',
    aiRenderGenerate: 'AI Render generieren',
    aiRenderSuccess: 'AI Render abgeschlossen!',
    aiRenderError: 'Generierung fehlgeschlagen',
    aiRenderCopy: 'In Zwischenablage kopieren',
    aiRenderDownload: 'Herunterladen',
    aiRenderRetry: 'Erneut versuchen',
    aiRenderClose: 'Schliessen',
    aiRenderPromptLabel: 'Prompt:',
    aiRenderTakingLonger: 'Dauert langer als erwartet...',
  },
  nl: {
    upload: 'Uploaden',
    recent: 'Recent',
    removeBg: 'Verwijder achtergrond',
    vectorize: 'Vectoriseren',
    crop: 'Bijsnijden',
    clear: 'Wissen',
    undo: 'Ongedaan',
    save: 'Opslaan',
    technique: 'Techniek',
    colors: 'Kleuren',
    full: 'Vol',
    back: 'Terug',
    select: 'Selecteer',
    move: 'Verplaatsen',
    delete: 'Verwijderen',
    zoomIn: 'Inzoomen',
    zoomOut: 'Uitzoomen',
    resetView: 'Weergave resetten',
    applyCrop: 'Bijsnijden toepassen',
    cancelCrop: 'Annuleren',
    magic3d: 'Magic 3D',
    magicLogo: 'Magic Logotool',
    selectTechnique: 'Kies techniek',
    selectColors: 'Kies kleuren',
    processing: 'Verwerken...',
    removingBg: 'Achtergrond verwijderen...',
    vectorizing: 'Vectoriseren...',
    noTechniques: 'Geen technieken beschikbaar',
    dropFilesHere: 'Sleep bestanden hierheen',
    orClickToUpload: 'of klik om te uploaden',
    backTo2D: 'Terug naar 2D',
    openFullScreen: 'Volledig scherm',
    magic3dPreview: 'Magic 3D Preview',
    clickOrDragToUpload: 'Klik hier of sleep een logo om te uploaden',
    configureProduct: 'Configureer uw product',
    selectPrintTechnique: 'Selecteer bedrukkingstechniek',
    numberOfColors: 'Aantal kleuren',
    colorVariants: 'Kleurvarianten',
    oneColor: '1 kleur',
    maxColors: 'Max {count} kleuren',
    noImageFound: 'Geen afbeelding gevonden',
    engraving: 'gravering',
    removeBgError: 'Fout bij verwijderen achtergrond. Controleer API key of probeer later opnieuw.',
    xColors: '{count} kleuren',
    fullColor: 'Volledige kleur',
    noImageForRemoveBg: 'Geen afbeelding gevonden om achtergrond te verwijderen',
    noImageForVectorize: 'Geen afbeelding gevonden om te vectoriseren',
    vectorizeError: 'Fout bij vectoriseren. Probeer een andere afbeelding.',
    noImageForCrop: 'Geen afbeelding gevonden om te croppen',
    cropOutsideImage: 'Crop selectie valt buiten de afbeelding',
    productPreview: 'Productvoorbeeld',
    basicMode: 'Basic',
    advancedMode: 'Advanced',
    logoSize: 'Logo grootte',
    copyLogo: 'Kopieer Artwork',
    copySvg: 'SVG kopieren',
    copied: 'Gekopieerd!',
    copyError: 'Kopieren mislukt',
    noLogoToCopy: 'Geen logo om te kopieren',
    copy3D: 'Kopieer 3D',
    copying3D: '3D kopieren...',
    no3DModel: 'Geen 3D model beschikbaar',
    downloadPdf: 'PDF',
    generatingPdf: 'PDF maken...',
    addToOrder: 'Toevoegen aan bestelling',
    addingToOrder: 'Wordt toegevoegd...',
    addedToOrder: 'Toegevoegd!',
    addToOrderError: 'Fout bij toevoegen aan bestelling',
    noLogoForOrder: 'Upload eerst een logo',
    aiRender: 'AI Render',
    aiRendering: 'AI genereert...',
    aiRenderReady: 'Klaar om te genereren',
    aiRenderGenerate: 'AI Render genereren',
    aiRenderSuccess: 'AI Render voltooid!',
    aiRenderError: 'Genereren mislukt',
    aiRenderCopy: 'Kopieren naar klembord',
    aiRenderDownload: 'Downloaden',
    aiRenderRetry: 'Opnieuw proberen',
    aiRenderClose: 'Sluiten',
    aiRenderPromptLabel: 'Prompt:',
    aiRenderTakingLonger: 'Duurt langer dan verwacht...',
  },
  en: {
    upload: 'Upload',
    recent: 'Recent',
    removeBg: 'Remove BG',
    vectorize: 'Vectorize',
    crop: 'Crop',
    clear: 'Clear',
    undo: 'Undo',
    save: 'Save',
    technique: 'Technique',
    colors: 'Colors',
    full: 'Full',
    back: 'Back',
    select: 'Select',
    move: 'Move',
    delete: 'Delete',
    zoomIn: 'Zoom In',
    zoomOut: 'Zoom Out',
    resetView: 'Reset View',
    applyCrop: 'Apply Crop',
    cancelCrop: 'Cancel',
    magic3d: 'Magic 3D',
    magicLogo: 'Magic Logotool',
    selectTechnique: 'Select technique',
    selectColors: 'Select colors',
    processing: 'Processing...',
    removingBg: 'Removing background...',
    vectorizing: 'Vectorizing...',
    noTechniques: 'No techniques available',
    dropFilesHere: 'Drop files here',
    orClickToUpload: 'or click to upload',
    backTo2D: 'Back to 2D',
    openFullScreen: 'Open Full Screen',
    magic3dPreview: 'Magic 3D Preview',
    clickOrDragToUpload: 'Click here or drag a logo to upload',
    configureProduct: 'Configure your product',
    selectPrintTechnique: 'Select print technique',
    numberOfColors: 'Number of colors',
    colorVariants: 'Color variants',
    oneColor: '1 color',
    maxColors: 'Max {count} colors',
    noImageFound: 'No image found',
    engraving: 'engraving',
    removeBgError: 'Error removing background. Check API key or try again later.',
    xColors: '{count} colors',
    fullColor: 'Full color',
    noImageForRemoveBg: 'No image found to remove background',
    noImageForVectorize: 'No image found to vectorize',
    vectorizeError: 'Error vectorizing. Try a different image.',
    noImageForCrop: 'No image found to crop',
    cropOutsideImage: 'Crop selection falls outside the image',
    productPreview: 'Product preview',
    basicMode: 'Basic',
    advancedMode: 'Advanced',
    logoSize: 'Logo Size',
    copyLogo: 'Copy Artwork',
    copySvg: 'Copy SVG',
    copied: 'Copied!',
    copyError: 'Copy failed',
    noLogoToCopy: 'No logo to copy',
    copy3D: 'Copy 3D',
    copying3D: 'Copying 3D...',
    no3DModel: 'No 3D model available',
    downloadPdf: 'PDF',
    generatingPdf: 'Generating PDF...',
    addToOrder: 'Add to Order',
    addingToOrder: 'Adding...',
    addedToOrder: 'Added!',
    addToOrderError: 'Error adding to order',
    noLogoForOrder: 'Please upload a logo first',
    aiRender: 'AI Render',
    aiRendering: 'AI generating...',
    aiRenderReady: 'Ready to generate',
    aiRenderGenerate: 'Generate AI Render',
    aiRenderSuccess: 'AI Render complete!',
    aiRenderError: 'Generation failed',
    aiRenderCopy: 'Copy to Clipboard',
    aiRenderDownload: 'Download',
    aiRenderRetry: 'Try Again',
    aiRenderClose: 'Close',
    aiRenderPromptLabel: 'Prompt:',
    aiRenderTakingLonger: 'Taking longer than expected...',
  },
};

// Get cached language or default to German
const getCachedLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem('magic_logo_language');
    if (cached && (cached === 'de' || cached === 'nl' || cached === 'en')) {
      return cached;
    }
  }
  return 'nl';
};

// Editor mode type
type EditorMode = 'basic' | 'advanced';

// Get cached editor mode or default to basic
const getCachedEditorMode = (): EditorMode => {
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem('magic_logo_editor_mode');
    if (cached && (cached === 'basic' || cached === 'advanced')) {
      return cached;
    }
  }
  return 'basic';
};

// Determine 3D viewer URL based on environment
const get3DViewerUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost) {
    return 'http://localhost:10052';
  }
  // Use spranz-3d.magiceverse.online for production
  return 'https://spranz-3d.magiceverse.online';
};

// Scale factor for API printArea dimensions (set to 1.0 = no adjustment)
// Backend uses different internal scales but output is already calibrated for display
const PRINT_AREA_SCALE_COMPENSATION = 1.0;

// Detect if an image is predominantly white (for adjusting canvas background)
const isImagePredominantlyWhite = (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(false);
        return;
      }

      // Sample at reduced size for performance
      const sampleSize = 100;
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
      const data = imageData.data;

      let whitePixels = 0;
      let totalNonTransparent = 0;

      // Check each pixel
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip transparent pixels
        if (a < 128) continue;

        totalNonTransparent++;

        // Consider pixel "white" if all channels are above 240
        if (r > 240 && g > 240 && b > 240) {
          whitePixels++;
        }
      }

      // If more than 50% of non-transparent pixels are white, consider it a white logo
      const whiteRatio = totalNonTransparent > 0 ? whitePixels / totalNonTransparent : 0;
      resolve(whiteRatio > 0.5);
    };
    img.onerror = () => resolve(false);
    img.src = imageUrl;
  });
};

// Canvas will be sized dynamically

// Helper component to load recent logo thumbnails via base64 API
const RecentLogoThumbnail: React.FC<{ logo: { filePath: string; originalFilename: string } }> = ({ logo }) => {
  const [src, setSrc] = useState<string>('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="10">Loading...</text></svg>');

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/logos/base64?path=${encodeURIComponent(logo.filePath)}`);
        const data = await response.json();
        if (data.success && data.data?.base64) {
          const base64Url = data.data.base64.startsWith('data:') ? data.data.base64 : `data:image/png;base64,${data.data.base64}`;
          setSrc(base64Url);
        }
      } catch (error) {
        setSrc('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23eee" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">Logo</text></svg>');
      }
    };
    loadThumbnail();
  }, [logo.filePath]);

  return (
    <img
      src={src}
      alt={logo.originalFilename}
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
};

export const MagicLogoModal: React.FC<MagicLogoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onMagic3D,
  onVariantChange,
  product,
  initialLogo,
  techniques: techniquesProp = [],
  isProductLoading = false,
  quantity: quantityProp,
  initialTechnique: initialTechniqueProp,
  mode = 'order',
  callbackUrl,
  customerId: customerIdProp,
  basketId: basketIdProp,
}) => {
  // Debug: Log product variants
  console.log('[MagicLogoModal] product:', product?.sku, 'variants:', product?.variants?.length || 0, product?.variants);

  // Use techniques from props - don't fall back to defaults
  const techniques = techniquesProp;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const iframe3DRef = useRef<HTMLIFrameElement>(null);
  // Promise cache for deduplicating vectorize calls
  const vectorizePromiseCache = useRef<Map<string, Promise<string>>>(new Map());
  const initialTechniqueRef = useRef<string>(techniques[0]?.code || '');
  const prevEditorModeRef = useRef<EditorMode | null>(null);

  const [selectedTechnique, setSelectedTechnique] = useState<string>(techniques[0]?.code || '');
  const [selectedColors, setSelectedColors] = useState<string>('3');

  // Update selectedTechnique when techniques prop changes (e.g., after API data loads)
  // If initialTechniqueProp is set and exists in techniques list, use that
  useEffect(() => {
    if (techniques.length > 0) {
      if (initialTechniqueProp) {
        const match = techniques.find(t => t.code === initialTechniqueProp);
        if (match) {
          setSelectedTechnique(match.code);
          initialTechniqueRef.current = match.code;
          return;
        }
      }
      if (!selectedTechnique) {
        setSelectedTechnique(techniques[0].code);
        initialTechniqueRef.current = techniques[0].code;
      }
    }
  }, [techniques, selectedTechnique, initialTechniqueProp]);

  const [slideOpen, setSlideOpen] = useState(false);
  const [slide3DOpen, setSlide3DOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [hasLogo, setHasLogo] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  // Track logo position relative to print area (0-1 range, 0.5 = centered)
  const [logoRelativePos, setLogoRelativePos] = useState<{
    x: number;      // 0 = left edge, 0.5 = center, 1 = right edge
    y: number;      // 0 = top edge, 0.5 = center, 1 = bottom edge
    scaleX: number; // logo width as fraction of print area width (0-1)
    scaleY: number; // logo height as fraction of print area height (0-1)
  }>({ x: 0.5, y: 0.5, scaleX: 1.0, scaleY: 1.0 });
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [isVectorizing, setIsVectorizing] = useState(false);
  const [vectorizeProgress, setVectorizeProgress] = useState(0);
  const [showVectorizeModal, setShowVectorizeModal] = useState(false);
  const [currentSpell, setCurrentSpell] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  const [basicLogoUrl, setBasicLogoUrl] = useState<string | null>(null);
  const [laserProcessedLogoUrl, setLaserProcessedLogoUrl] = useState<string | null>(null);
  const [basicLogoSize, setBasicLogoSize] = useState(100); // percentage 10-100
  const [savedCanvasJson, setSavedCanvasJson] = useState<string | null>(null); // Save canvas state when leaving advanced mode
  const [canvasBackground, setCanvasBackground] = useState<string>('#ffffff'); // Gray for white logos
  const [copyFeedback, setCopyFeedback] = useState<'logo' | 'svg' | '3d' | 'pdf' | null>(null);
  const [isCopying3D, setIsCopying3D] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesModalData, setFilesModalData] = useState<{
    transactionId: string;
    productSku: string;
    techniqueName: string;
    quantity: number;
    files: {
      screenshot?: string;
      screenshot_hq?: string;
      logo_original?: string;
      pdf?: string;
    };
  } | null>(null);
  const screenshot3DResolveRef = useRef<((dataUrl: string) => void) | null>(null);
  const [selectedTextColor, setSelectedTextColor] = useState<string>('#002B51');
  const [selectedFontFamily, setSelectedFontFamily] = useState<string>('Arial');
  const [isTextSelected, setIsTextSelected] = useState(false);
  const [isBucketMode, setIsBucketMode] = useState(false);
  const [bucketColor, setBucketColor] = useState<string>("#000000");
  const [paletteColors, setPaletteColors] = useState<string[]>([]);

  // AI Render state
  const [showAiRenderOverlay, setShowAiRenderOverlay] = useState(false);
  const [aiRenderStep, setAiRenderStep] = useState<'ready' | 'generating' | 'success' | 'error'>('ready');
  const [aiRenderScreenshot, setAiRenderScreenshot] = useState<string | null>(null);
  const [aiRenderPrompt, setAiRenderPrompt] = useState('');
  const [aiRenderTaskId, setAiRenderTaskId] = useState<string | null>(null);
  const [aiRenderProgress, setAiRenderProgress] = useState(0);
  const [aiRenderResult, setAiRenderResult] = useState<string | null>(null);
  const [aiRenderError, setAiRenderError] = useState<string | null>(null);
  const [aiRenderElapsed, setAiRenderElapsed] = useState(0);
  const aiRenderPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const aiRenderTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [aiRenderOption, setAiRenderOption] = useState<1 | 2>(1);
  const [aiRenderOriginalImage, setAiRenderOriginalImage] = useState<string | null>(null);
  // Option 2: Video generation (two-step pipeline)
  const [aiVideoPhase, setAiVideoPhase] = useState<'ready' | 'generating-image' | 'image-done' | 'generating-video' | 'success' | 'error'>('ready');
  const [aiVideoMoodShot, setAiVideoMoodShot] = useState<string | null>(null);
  const [aiVideoResult, setAiVideoResult] = useState<string | null>(null);
  const [aiVideoError, setAiVideoError] = useState<string | null>(null);
  const [aiVideoDuration, setAiVideoDuration] = useState<'6' | '10'>('6');
  const [aiVideoResolution, setAiVideoResolution] = useState<'480p' | '720p'>('720p');
  const [aiVideoPrompt, setAiVideoPrompt] = useState('Slow cinematic camera pull-out shot, starting close on the product and gradually revealing the full scene. Subtle lighting changes, soft reflections, and a premium lifestyle atmosphere. Smooth steady movement, no rotation.');
  const aiVideoPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const aiVideoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [showRecentPanel, setShowRecentPanel] = useState(false);
  const [recentLogos, setRecentLogos] = useState<Array<{ id: number; fileHash: string; originalFilename: string; mimeType: string; filePath: string; thumbnailUrl?: string }>>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const cropRectRef = useRef<fabric.Rect | null>(null);

  // Preview zoom state for basic mode
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Basic mode logo drag state
  const [isDraggingBasicLogo, setIsDraggingBasicLogo] = useState(false);
  const basicDragStartRef = useRef<{ mouseX: number; mouseY: number; logoX: number; logoY: number } | null>(null);
  const basicPreviewWrapperRef = useRef<HTMLDivElement>(null);

  // Slide panel 2D preview zoom
  const slidePreviewRef = useRef<HTMLDivElement>(null);

  // Fullscreen overlay state
  const [fullscreen2D, setFullscreen2D] = useState(false);
  const [fullscreen3D, setFullscreen3D] = useState(false);
  const [fullscreenTransform, setFullscreenTransform] = useState({ scale: 1, x: 0, y: 0 });
  const fullscreenContentRef = useRef<HTMLDivElement>(null);

  // Logo Wizard state
  const [showWizard, setShowWizard] = useState(false);

  const [wizardLogoBase64, setWizardLogoBase64] = useState<string | null>(null);
  const [wizardOriginalFormat, setWizardOriginalFormat] = useState<string | undefined>(undefined);
  const [wizardOriginalBase64, setWizardOriginalBase64] = useState<string | null>(null);
  const updateLogoPreviewRef = useRef<(() => void) | null>(null);

  // Best practice fonts for logo design
  const availableFonts = [
    'Arial',
    'Helvetica',
    'Verdana',
    'Roboto',
    'Open Sans',
    'Montserrat',
    'Lato',
    'Oswald',
    'Raleway',
    'Poppins',
  ];

  // Common colors for logos
  const textColors = [
    '#000000', // Black
    '#FFFFFF', // White
    '#002B51', // Brand blue
    '#E63946', // Red
    '#2A9D8F', // Teal
    '#E9C46A', // Yellow/Gold
    '#264653', // Dark teal
    '#F4A261', // Orange
    '#8B5CF6', // Purple
    '#10B981', // Green
  ];

  // Language state with caching
  const [language, setLanguageState] = useState<Language>(getCachedLanguage);

  // Set language and cache it
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('magic_logo_language', lang);
  }, []);

  // Editor mode state (basic vs advanced) with caching
  const [editorMode, setEditorModeState] = useState<EditorMode>(getCachedEditorMode);

  // Add to Order state
  const [addToOrderStatus, setAddToOrderStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [lastTransactionId, setLastTransactionId] = useState<string | null>(null);

  // Set editor mode and cache it
  const setEditorMode = useCallback((mode: EditorMode) => {
    setEditorModeState(mode);
    localStorage.setItem('magic_logo_editor_mode', mode);
  }, []);

  // Translate function - returns translated text for current language
  const t = useCallback((key: TranslationKey): string => {
    return translations[language][key] || translations.en[key] || key;
  }, [language]);

  // Vectorize logo for Laser techniques (convert to 1-color gray)
  // Uses promise cache to deduplicate concurrent calls with same input
  const vectorizeForLaser = useCallback(async (logoDataUrl: string): Promise<string> => {
    // Create a simple hash key from the first 100 chars of the data URL
    const cacheKey = logoDataUrl.slice(0, 100);

    // Check if we already have a pending promise for this exact input
    const existingPromise = vectorizePromiseCache.current.get(cacheKey);
    if (existingPromise) {
      console.log('Vectorize: using cached promise');
      return existingPromise;
    }

    // Create new promise and cache it
    const fetchPromise = (async () => {
      try {
        const response = await fetch('/api/logos/vectorize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Data: logoDataUrl,
            threshold: 50,
            turdsize: 2,
            alphamax: 1,
            colorMode: false,
            numberOfColors: 1,
            color: '#7A8288', // Stainless steel gray
          }),
        });
        const result = await response.json();
        if (result.success && result.data?.svg) {
          return result.data.svg;
        }
      } catch (error) {
        console.error('Vectorize for laser failed:', error);
      } finally {
        // Clean up cache after a short delay to allow late subscribers
        setTimeout(() => vectorizePromiseCache.current.delete(cacheKey), 500);
      }
      return logoDataUrl;
    })();

    vectorizePromiseCache.current.set(cacheKey, fetchPromise);
    return fetchPromise;
  }, []);

  // Cache for laser-vectorized logo to avoid re-vectorizing on every move
  const lastVectorizedRef = useRef<{ src: string; result: string } | null>(null);

  // Function to send logo to 3D viewer iframe via postMessage
  const sendLogoTo3DViewer = useCallback(async (logoDataUrl: string | null, position?: { x: number; y: number; scaleX: number; scaleY: number }, skipVectorize?: boolean) => {
    if (!iframe3DRef.current?.contentWindow || !logoDataUrl) return;

    // Check if selected technique is Laser - if so, vectorize first
    const isLaser = selectedTechnique?.startsWith('L');
    let finalLogoUrl = logoDataUrl;

    if (isLaser && !skipVectorize) {
      // Check cache first
      if (lastVectorizedRef.current?.src === logoDataUrl) {
        finalLogoUrl = lastVectorizedRef.current.result;
      } else {
        finalLogoUrl = await vectorizeForLaser(logoDataUrl);
        lastVectorizedRef.current = { src: logoDataUrl, result: finalLogoUrl };
      }
    } else if (isLaser && lastVectorizedRef.current) {
      // During movement, use cached vectorized version
      finalLogoUrl = lastVectorizedRef.current.result;
    }

    // Apply scale correction for 3D: 2D uses API printArea dimensions (4.19 px/mm scale),
    // while 3D uses print_area mm dimensions directly. The ratio is ~1.48x.
    // Boost the scale to match 2D visual appearance.
    const SCALE_CORRECTION_3D = 1.48; // Match API scale ratio (4.19/2.833)
    const positionData = position || logoRelativePos;
    const correctedPosition = {
      ...positionData,
      scaleX: positionData.scaleX * SCALE_CORRECTION_3D,
      scaleY: positionData.scaleY * SCALE_CORRECTION_3D,
    };

    iframe3DRef.current.contentWindow.postMessage({
      type: 'magic_logo_update',
      logoDataUrl: finalLogoUrl,
      technique: selectedTechnique,
      sku: product?.sku,
      logoPosition: correctedPosition,
    }, '*');
  }, [selectedTechnique, vectorizeForLaser, product?.sku, logoRelativePos]);

  // Send logo updates to 3D viewer when logoPreviewUrl changes (real-time sync)
  // Always send updates, even if 3D slide is closed - iframe stays loaded in background
  useEffect(() => {
    if (logoPreviewUrl) {
      sendLogoTo3DViewer(logoPreviewUrl);
    }
  }, [logoPreviewUrl, sendLogoTo3DViewer]);

  // Send technique changes to 3D viewer via postMessage (avoids iframe reload)
  useEffect(() => {
    if (iframe3DRef.current?.contentWindow && selectedTechnique) {
      sendLogoTo3DViewer(logoPreviewUrl);
    }
  }, [selectedTechnique, logoPreviewUrl, sendLogoTo3DViewer]);

  // Send SKU/variant changes to 3D viewer via postMessage (for color variant switching)
  useEffect(() => {
    if (iframe3DRef.current?.contentWindow && product?.sku) {
      sendLogoTo3DViewer(logoPreviewUrl);
    }
  }, [product?.sku, logoPreviewUrl, sendLogoTo3DViewer]);

  // Send logo position updates to 3D viewer when logo is moved/resized
  useEffect(() => {
    if (iframe3DRef.current?.contentWindow && logoPreviewUrl && logoRelativePos) {
      sendLogoTo3DViewer(logoPreviewUrl, logoRelativePos, true); // skipVectorize during movement
    }
  }, [logoRelativePos, logoPreviewUrl, sendLogoTo3DViewer]);

  // Process logo for Laser techniques (vectorize to gray) for 2D preview
  useEffect(() => {
    const processLogoForLaser = async () => {
      const isLaser = selectedTechnique?.startsWith('L');
      if (isLaser && logoPreviewUrl) {
        const processed = await vectorizeForLaser(logoPreviewUrl);
        setLaserProcessedLogoUrl(processed);
      } else {
        setLaserProcessedLogoUrl(null);
      }
    };
    processLogoForLaser();
  }, [selectedTechnique, logoPreviewUrl, vectorizeForLaser]);

  // Show initial loader until product is loaded (with minimum 1.5 seconds)
  useEffect(() => {
    if (isOpen) {
      setShowInitialLoader(true);

      // Always show loader for at least 1.5 seconds for smooth UX
      const minTimer = setTimeout(() => {
        // Only hide if product is loaded
        if (!isProductLoading) {
          setShowInitialLoader(false);
        }
      }, 1500);

      return () => clearTimeout(minTimer);
    }
  }, [isOpen]);

  // Hide loader when product loading completes (after minimum time)
  useEffect(() => {
    if (!isProductLoading && isOpen) {
      // Add small delay to ensure minimum display time has passed
      const timer = setTimeout(() => {
        setShowInitialLoader(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isProductLoading, isOpen]);

  // Get current technique object
  const currentTechnique = useMemo(() => {
    return techniques.find(t => t.code === selectedTechnique) || techniques[0];
  }, [selectedTechnique, techniques]);

  // Generate color options dynamically based on technique maxColors
  const colorOptions = useMemo(() => {
    const maxColors = currentTechnique?.maxColors || 4;
    const options: { id: string; name: string; count: number }[] = [];

    for (let i = 1; i <= maxColors; i++) {
      options.push({
        id: String(i),
        name: i === 1 ? t('oneColor') : t('xColors').replace('{count}', String(i)),
        count: i,
      });
    }

    // Only add Full Color option if maxColors > 4 (full color techniques)
    if (maxColors >= 8) {
      options.push({
        id: 'full',
        name: t('fullColor'),
        count: -1,
      });
    }

    return options;
  }, [currentTechnique, t]);

  // Reset selectedColors when technique changes if current selection is invalid
  // Also auto-rotate logo if print area orientation doesn't match logo orientation
  useEffect(() => {
    const maxColors = currentTechnique?.maxColors || 4;
    const currentCount = selectedColors === 'full' ? Infinity : parseInt(selectedColors);

    // If current selection exceeds maxColors, reset to '1'
    if (currentCount > maxColors && selectedColors !== 'full') {
      setSelectedColors('1');
    }
    // If 'full' is selected but technique doesn't support it (maxColors < 8), reset to max
    if (selectedColors === 'full' && maxColors < 8) {
      setSelectedColors(String(maxColors));
    }

    // Redraw print area when technique changes
    if (fabricCanvasRef.current && (fabricCanvasRef.current as any).drawPrintArea) {
      (fabricCanvasRef.current as any).drawPrintArea(currentTechnique);

      // Auto-rotate logo if orientations don't match
      const canvas = fabricCanvasRef.current;
      const logoObjects = canvas.getObjects().filter((obj: fabric.Object) => !(obj as any).isPrintArea);


      // Apply single color effect ONLY for P (Preging/Embossing) techniques on canvas
      // L (Laser) keeps original colors on canvas - effect is only shown in preview
      // E (Embroidery/Borduren) applies stitch effect
      const isEmbossingTechnique = currentTechnique?.code?.startsWith('P');
      const isLaserTechnique = currentTechnique?.code?.startsWith('L');
      const isEmbroideryTechnique = currentTechnique?.code === 'E' || currentTechnique?.code?.toLowerCase().includes('embroid') || currentTechnique?.name?.toLowerCase().includes('bordur') || currentTechnique?.name?.toLowerCase().includes('embroid');
      console.log('Technique check - code:', currentTechnique?.code, 'isEmboss:', isEmbossingTechnique, 'isLaser:', isLaserTechnique, 'isEmbroidery:', isEmbroideryTechnique, 'logoObjects:', logoObjects.length);

      // Helper function to apply single color filters to an image (only for Emboss)
      const applyEmbossToImage = (imgObj: fabric.Image) => {
        if (isEmbossingTechnique) {
          console.log('Applying emboss filter to image');
          imgObj.filters = [
            new fabric.Image.filters.Grayscale(),
            new fabric.Image.filters.Brightness({ brightness: 0.1 }),
            new fabric.Image.filters.Contrast({ contrast: 0.3 }),
          ];
        } else {
          imgObj.filters = [];
        }
        imgObj.applyFilters();
      };

      // Helper to apply single color effect to SVG groups (only for Emboss)
      const applyEmbossToGroup = (group: fabric.Group) => {
        const groupObjects = group.getObjects();

        if (isEmbossingTechnique) {
          console.log('Applying emboss effect to SVG group');

          // Emboss: dark gray/black color
          const fillColor = '#333333';

          // Store original values and set single gray color for all paths
          groupObjects.forEach((groupObj: fabric.Object) => {
            if (groupObj.type === 'path' || groupObj.type === 'polygon' || groupObj.type === 'rect' || groupObj.type === 'circle') {
              (groupObj as any)._originalFill = (groupObj as any)._originalFill || (groupObj as any).fill;
              (groupObj as any)._originalStroke = (groupObj as any)._originalStroke || (groupObj as any).stroke;
              (groupObj as any)._originalStrokeWidth = (groupObj as any)._originalStrokeWidth ?? (groupObj as any).strokeWidth ?? 0;

              // Single color for all paths
              groupObj.set({
                fill: fillColor,
                stroke: null,
                strokeWidth: 0,
              });
            }
          });

          // Remove any shadow for clean single color look
          group.set({ shadow: null });

        } else {
          // Remove effect - restore original colors (for non-emboss techniques)
          groupObjects.forEach((groupObj: fabric.Object) => {
            if ((groupObj as any)._originalFill !== undefined) {
              groupObj.set({
                fill: (groupObj as any)._originalFill,
                stroke: (groupObj as any)._originalStroke || null,
                strokeWidth: (groupObj as any)._originalStrokeWidth || 0,
              });
            }
          });
          group.set({ shadow: null });
        }
      };

      // Helper to apply embroidery stitch effect to SVG groups
      const applyEmbroideryToGroup = (group: fabric.Group) => {
        const groupObjects = group.getObjects();

        if (isEmbroideryTechnique) {
          console.log('Applying embroidery stitch effect to SVG group');

          // Apply stitch-like effect to each path
          groupObjects.forEach((groupObj: fabric.Object) => {
            if (groupObj.type === 'path' || groupObj.type === 'polygon' || groupObj.type === 'rect' || groupObj.type === 'circle') {
              // Store original values
              (groupObj as any)._originalFill = (groupObj as any)._originalFill || (groupObj as any).fill;
              (groupObj as any)._originalStroke = (groupObj as any)._originalStroke || (groupObj as any).stroke;
              (groupObj as any)._originalStrokeWidth = (groupObj as any)._originalStrokeWidth ?? (groupObj as any).strokeWidth ?? 0;

              const currentFill = (groupObj as any)._originalFill || '#000000';

              // Create embroidery effect: keep original color but add texture-like stroke
              groupObj.set({
                fill: currentFill,
                stroke: currentFill,
                strokeWidth: 0.8,
                strokeDashArray: [4, 1], // Horizontal stitch dashes
              });
            }
          });

          // Add subtle shadow for 3D thread effect
          group.set({
            shadow: new fabric.Shadow({
              color: 'rgba(0,0,0,0.3)',
              blur: 1,
              offsetX: 0.5,
              offsetY: 0.5,
            }),
          });

        } else {
          // Remove embroidery effect - restore original
          groupObjects.forEach((groupObj: fabric.Object) => {
            if ((groupObj as any)._originalFill !== undefined) {
              groupObj.set({
                fill: (groupObj as any)._originalFill,
                stroke: (groupObj as any)._originalStroke || null,
                strokeWidth: (groupObj as any)._originalStrokeWidth || 0,
                strokeDashArray: undefined,
              });
            }
          });
        }
      };

      // Helper to apply embroidery effect to images
      const applyEmbroideryToImage = (imgObj: fabric.Image) => {
        if (isEmbroideryTechnique) {
          console.log('Applying embroidery filter to image');
          // For images, we add slight texture effect
          imgObj.filters = [
            new fabric.Image.filters.Contrast({ contrast: 0.1 }),
          ];
          imgObj.set({
            shadow: new fabric.Shadow({
              color: 'rgba(0,0,0,0.3)',
              blur: 1,
              offsetX: 0.5,
              offsetY: 0.5,
            }),
          });
        } else if (!isEmbossingTechnique) {
          imgObj.filters = [];
          imgObj.set({ shadow: null });
        }
        imgObj.applyFilters();
      };

      logoObjects.forEach((obj: fabric.Object) => {
        console.log('Object type:', obj.type);
        if (obj.type === 'image') {
          applyEmbossToImage(obj as fabric.Image);
          applyEmbroideryToImage(obj as fabric.Image);
        } else if (obj.type === 'group') {
          applyEmbossToGroup(obj as fabric.Group);
          applyEmbroideryToGroup(obj as fabric.Group);
        }
      });
      canvas.renderAll();
      console.log('Canvas rendered, isEmbossing:', isEmbossingTechnique, 'isEmbroidery:', isEmbroideryTechnique);

      if (logoObjects.length > 0 && currentTechnique?.dimensions) {
        // Resize logo to fill 100% of the new print area (keep horizontal)
        const printAreaBounds = (canvas as any).printAreaBounds;
        if (printAreaBounds) {
          logoObjects.forEach((logo) => {
            // Always keep logo horizontal (no rotation)
            logo.set({ angle: 0 });

            // Get current bounds
            const logoBounds = logo.getBoundingRect();

            // Calculate scale to fill 100% of print area (maintain aspect ratio)
            const scaleX = printAreaBounds.width / logoBounds.width;
            const scaleY = printAreaBounds.height / logoBounds.height;
            const scaleFactor = Math.min(scaleX, scaleY);

            // Apply new scale
            logo.set({
              scaleX: (logo.scaleX || 1) * scaleFactor,
              scaleY: (logo.scaleY || 1) * scaleFactor,
            });

            // Center in print area
            logo.set({
              left: printAreaBounds.left + printAreaBounds.width / 2,
              top: printAreaBounds.top + printAreaBounds.height / 2,
              originX: 'center',
              originY: 'center',
            });

            logo.setCoords();
          });
          canvas.renderAll();
        }

        // Update preview to sync 2D preview and 3D viewer
        setTimeout(() => {
          updateLogoPreviewRef.current?.();
        }, 100);
      }
    }
  }, [selectedTechnique, currentTechnique]);

  // Auto-open Magic Logo slide panel only in Advanced mode when:
  // 1. Technique is selected
  // 2. Colors are selected
  // 3. Logo is uploaded
  useEffect(() => {
    // Only auto-open slide in Advanced mode, not in Basic mode
    if (editorMode === 'advanced' && hasLogo && selectedTechnique && selectedColors && !slideOpen) {
      setSlideOpen(true);
    }
  }, [hasLogo, selectedTechnique, selectedColors, editorMode]);

  // Initialize Fabric.js Canvas
  useEffect(() => {
    if (!isOpen || !canvasRef.current || !containerRef.current) return;

    // Small delay to ensure container is rendered
    const timer = setTimeout(() => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
      }

      // Get container dimensions
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const width = Math.floor(rect.width - 40); // padding
      const height = Math.floor(rect.height - 40); // padding

      setCanvasSize({ width, height });

      const canvas = new fabric.Canvas(canvasRef.current, {
        width: width,
        height: height,
        backgroundColor: '#ffffff',
        selection: true,
        preserveObjectStacking: true,
      });

      // Selection styling
      fabric.Object.prototype.set({
        cornerColor: '#002B51',
        cornerStyle: 'circle',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: '#002B51',
        borderScaleFactor: 2,
        padding: 10,
      });

      fabricCanvasRef.current = canvas;
      setIsLoading(false);

      // Draw print area boundary on canvas
      const drawPrintArea = (tech: Technique | undefined) => {
        if (!tech || !canvas) return;

        // Remove ALL existing print area objects
        const existingObjects = canvas.getObjects().filter((obj: fabric.Object) => (obj as any).isPrintArea);
        existingObjects.forEach((obj) => canvas.remove(obj));

        // Use printAreaWidth/Height if available (pixel values from API)
        // Otherwise fall back to parsing dimensions string (format: "35x25", "223x50", or "150 x 150 mm")
        let printWidth: number;
        let printHeight: number;

        if (tech.printAreaWidth && tech.printAreaHeight) {
          printWidth = tech.printAreaWidth;
          printHeight = tech.printAreaHeight;
          console.log(`[drawPrintArea] Using pixel values: ${printWidth}x${printHeight}`);
        } else {
          // Try parsing dimensions string - handle formats like "35x25" or "150 x 150 mm"
          const dims = tech.dimensions?.match(/(\d+)\s*x\s*(\d+)/i);
          if (!dims) return;
          printWidth = parseInt(dims[1]);
          printHeight = parseInt(dims[2]);
          console.log(`[drawPrintArea] Parsed from dimensions "${tech.dimensions}": ${printWidth}x${printHeight}`);
        }

        // Scale to canvas - use canvas dimensions to show print area proportionally
        // The print area should represent the actual printable region
        const canvasW = canvas.getWidth();
        const canvasH = canvas.getHeight();

        // Scale factor: map print area to fit nicely within canvas (leaving margin)
        const maxPrintDim = Math.max(printWidth, printHeight);
        const scaleFactor = Math.min(canvasW, canvasH) * 0.9 / maxPrintDim;

        const rectWidth = printWidth * scaleFactor;
        const rectHeight = printHeight * scaleFactor;

        // Create dashed rectangle for print area
        const printAreaRect = new fabric.Rect({
          left: (canvasW - rectWidth) / 2,
          top: (canvasH - rectHeight) / 2,
          width: rectWidth,
          height: rectHeight,
          fill: 'transparent',
          stroke: '#002B51',
          strokeWidth: 2,
          strokeDashArray: [10, 5],
          selectable: false,
          evented: false,
        });
        (printAreaRect as any).isPrintArea = true;

        // Add label
        const label = new fabric.Text(`${tech.code}: ${tech.dimensions}`, {
          left: (canvasW - rectWidth) / 2,
          top: (canvasH - rectHeight) / 2 - 25,
          fontSize: 14,
          fill: '#002B51',
          fontFamily: 'Arial',
          selectable: false,
          evented: false,
        });
        (label as any).isPrintArea = true;

        canvas.add(printAreaRect);
        canvas.add(label);
        canvas.sendToBack(printAreaRect);
        canvas.sendToBack(label);
        canvas.renderAll();

        // Store print area bounds for constraining objects
        (canvas as any).printAreaBounds = {
          left: (canvasW - rectWidth) / 2,
          top: (canvasH - rectHeight) / 2,
          right: (canvasW - rectWidth) / 2 + rectWidth,
          bottom: (canvasH - rectHeight) / 2 + rectHeight,
          width: rectWidth,
          height: rectHeight,
        };
      };

      // Initial draw
      if (techniques.length > 0) {
        drawPrintArea(techniques.find(t => t.code === selectedTechnique) || techniques[0]);
      }

      // Store drawPrintArea function for later use
      (canvas as any).drawPrintArea = drawPrintArea;

      // Constrain objects to stay within print area bounds
      const constrainToPrintArea = (obj: fabric.Object, constrainSize: boolean = false) => {
        if ((obj as any).isPrintArea) return; // Don't constrain print area itself

        const bounds = (canvas as any).printAreaBounds;
        if (!bounds) return;

        // First, constrain size if logo is bigger than print area
        if (constrainSize) {
          const objBounds = obj.getBoundingRect();
          if (objBounds.width > bounds.width || objBounds.height > bounds.height) {
            // Calculate scale to fit within print area
            const scaleX = bounds.width / objBounds.width;
            const scaleY = bounds.height / objBounds.height;
            const scaleFactor = Math.min(scaleX, scaleY) * 0.95; // 95% to leave margin

            obj.set({
              scaleX: (obj.scaleX || 1) * scaleFactor,
              scaleY: (obj.scaleY || 1) * scaleFactor,
            });
            obj.setCoords();
          }
        }

        const objBounds = obj.getBoundingRect();

        // Get object's actual position (considering origin)
        let newLeft = obj.left || 0;
        let newTop = obj.top || 0;

        // Constrain left edge
        if (objBounds.left < bounds.left) {
          newLeft += bounds.left - objBounds.left;
        }
        // Constrain right edge
        if (objBounds.left + objBounds.width > bounds.right) {
          newLeft -= (objBounds.left + objBounds.width) - bounds.right;
        }
        // Constrain top edge
        if (objBounds.top < bounds.top) {
          newTop += bounds.top - objBounds.top;
        }
        // Constrain bottom edge
        if (objBounds.top + objBounds.height > bounds.bottom) {
          newTop -= (objBounds.top + objBounds.height) - bounds.bottom;
        }

        obj.set({ left: newLeft, top: newTop });
        obj.setCoords();
      };

      // Add event listener for moving objects
      canvas.on('object:moving', (e) => {
        if (e.target) constrainToPrintArea(e.target);
      });

      // Constrain after scaling - also check size
      canvas.on('object:scaling', (e) => {
        if (e.target) constrainToPrintArea(e.target, true);
      });

      canvas.on('object:rotating', (e) => {
        if (e.target) constrainToPrintArea(e.target, true);
      });

      // Also constrain after any modification is complete
      canvas.on('object:modified', (e) => {
        if (e.target) constrainToPrintArea(e.target, true);
      });

      // Track text selection for formatting toolbar
      canvas.on('selection:created', (e) => {
        const selected = e.selected?.[0];
        if (selected && selected.type === 'i-text') {
          setIsTextSelected(true);
          setSelectedTextColor((selected as fabric.IText).fill as string || '#002B51');
          setSelectedFontFamily((selected as fabric.IText).fontFamily || 'Arial');
        } else {
          setIsTextSelected(false);
        }
      });

      canvas.on('selection:updated', (e) => {
        const selected = e.selected?.[0];
        if (selected && selected.type === 'i-text') {
          setIsTextSelected(true);
          setSelectedTextColor((selected as fabric.IText).fill as string || '#002B51');
          setSelectedFontFamily((selected as fabric.IText).fontFamily || 'Arial');
        } else {
          setIsTextSelected(false);
        }
      });

      canvas.on('selection:cleared', () => {
        setIsTextSelected(false);
      });

      // Helper function to export canvas without print area - cropped to logo only
      const exportCanvasPreview = () => {
        if (!fabricCanvasRef.current) return;
        const c = fabricCanvasRef.current;
        const allObjects = c.getObjects();
        const contentObjects = allObjects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);

        if (contentObjects.length > 0) {
          // Hide print area objects temporarily
          const printAreaObjects = allObjects.filter((obj: fabric.Object) => (obj as any).isPrintArea);
          printAreaObjects.forEach((obj) => obj.set('visible', false));

          // Store original background and make it transparent for export
          const originalBg = c.backgroundColor;
          c.backgroundColor = 'transparent';

          // Calculate bounding box of all content objects
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          contentObjects.forEach((obj) => {
            const bounds = obj.getBoundingRect();
            minX = Math.min(minX, bounds.left);
            minY = Math.min(minY, bounds.top);
            maxX = Math.max(maxX, bounds.left + bounds.width);
            maxY = Math.max(maxY, bounds.top + bounds.height);
          });

          // Calculate logo's relative position within print area
          const printBounds = (c as any).printAreaBounds;
          if (printBounds) {
            const logoWidth = maxX - minX;
            const logoHeight = maxY - minY;
            const logoCenterX = minX + logoWidth / 2;
            const logoCenterY = minY + logoHeight / 2;

            // Calculate relative position (0-1 within print area)
            const relX = (logoCenterX - printBounds.left) / printBounds.width;
            const relY = (logoCenterY - printBounds.top) / printBounds.height;
            const relScaleX = logoWidth / printBounds.width;
            const relScaleY = logoHeight / printBounds.height;

            setLogoRelativePos({
              x: Math.max(0, Math.min(1, relX)),
              y: Math.max(0, Math.min(1, relY)),
              scaleX: Math.min(1, relScaleX),
              scaleY: Math.min(1, relScaleY),
            });
          }

          // Add small padding
          const padding = 5;
          minX = Math.max(0, minX - padding);
          minY = Math.max(0, minY - padding);
          maxX = Math.min(c.getWidth(), maxX + padding);
          maxY = Math.min(c.getHeight(), maxY + padding);

          // Export only the cropped area containing the logo
          const dataUrl = c.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: 2,
            left: minX,
            top: minY,
            width: maxX - minX,
            height: maxY - minY,
          });
          setLogoPreviewUrl(dataUrl);

          // Restore background and show print area objects again
          c.backgroundColor = originalBg;
          printAreaObjects.forEach((obj) => obj.set('visible', true));
          c.renderAll();
        } else {
          setLogoPreviewUrl(null);
        }
      };

      // Add event listeners for canvas changes to update preview
      canvas.on('object:modified', () => setTimeout(exportCanvasPreview, 50));
      canvas.on('object:added', () => setTimeout(exportCanvasPreview, 50));
      canvas.on('object:removed', () => setTimeout(exportCanvasPreview, 50));

      // Throttled update during moving for real-time preview
      let moveThrottle: ReturnType<typeof setTimeout> | null = null;
      canvas.on('object:moving', () => {
        if (!moveThrottle) {
          moveThrottle = setTimeout(() => {
            exportCanvasPreview();
            moveThrottle = null;
          }, 100); // Update every 100ms during movement
        }
      });

      // Also update during scaling and rotating
      canvas.on('object:scaling', () => {
        if (!moveThrottle) {
          moveThrottle = setTimeout(() => {
            exportCanvasPreview();
            moveThrottle = null;
          }, 100);
        }
      });

      canvas.on('object:rotating', () => {
        if (!moveThrottle) {
          moveThrottle = setTimeout(() => {
            exportCanvasPreview();
            moveThrottle = null;
          }, 100);
        }
      });

      // Zoom-to-cursor functionality - handled in separate useEffect

      // Pan with middle mouse button or Alt+drag
      let isPanning = false;
      let lastPosX = 0;
      let lastPosY = 0;

      canvas.on('mouse:down', (opt) => {
        const evt = opt.e as MouseEvent;
        // Middle mouse button (button 1) or Alt key
        if (evt.button === 1 || evt.altKey) {
          isPanning = true;
          lastPosX = evt.clientX;
          lastPosY = evt.clientY;
          canvas.selection = false;
          canvas.setCursor('grab');
        }
      });

      canvas.on('mouse:move', (opt) => {
        if (isPanning) {
          const evt = opt.e as MouseEvent;
          const vpt = canvas.viewportTransform;
          if (vpt) {
            vpt[4] += evt.clientX - lastPosX;
            vpt[5] += evt.clientY - lastPosY;
            canvas.requestRenderAll();
            lastPosX = evt.clientX;
            lastPosY = evt.clientY;
            canvas.setCursor('grabbing');
          }
        }
      });

      canvas.on('mouse:up', () => {
        if (isPanning) {
          isPanning = false;
          canvas.selection = true;
          canvas.setCursor('default');
        }
      });

      // Load initial logo if provided
      if (initialLogo) {
        loadLogoToCanvas(initialLogo);
      }
    }, 150);

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !fabricCanvasRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.floor(rect.width - 40);
      const height = Math.floor(rect.height - 40);

      setCanvasSize({ width, height });
      fabricCanvasRef.current.setDimensions({ width, height });
      fabricCanvasRef.current.renderAll();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [isOpen, editorMode]);

  // Zoom-to-cursor functionality with mouse wheel
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    const canvas = fabricCanvasRef.current;

    console.log('[2D Zoom Setup] wrapper:', !!wrapper, 'canvas:', !!canvas, 'isLoading:', isLoading);

    if (!wrapper || !canvas || isLoading) return;

    console.log('[2D Zoom Setup] Attaching wheel event listener');

    const handleWheel = (e: WheelEvent) => {
      console.log('[2D Zoom] Wheel event received! deltaY:', e.deltaY);
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      let zoom = canvas.getZoom();
      console.log('[2D Zoom] Current zoom:', zoom);
      const zoomSpeed = 0.1;

      // Calculate new zoom level
      zoom *= delta > 0 ? (1 - zoomSpeed) : (1 + zoomSpeed);

      // Clamp zoom between 0.5x and 5x
      zoom = Math.max(0.5, Math.min(5, zoom));

      // Get the mouse position relative to the canvas
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Zoom to the point under the cursor
      canvas.zoomToPoint(new fabric.Point(x, y), zoom);
      canvas.requestRenderAll();

      console.log(`[2D Zoom] zoom=${zoom.toFixed(2)}, point=(${x.toFixed(0)}, ${y.toFixed(0)})`);
    };

    wrapper.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, editorMode, hasLogo, isLoading]); // Re-attach when canvas is ready

  // Zoom functionality for basic mode preview (product + logo)
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container || editorMode !== 'basic') return;

    const handlePreviewWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const zoomSpeed = 0.1;

      setPreviewZoom(prevZoom => {
        let newZoom = prevZoom * (delta > 0 ? (1 - zoomSpeed) : (1 + zoomSpeed));
        // Clamp zoom between 0.5x and 4x
        newZoom = Math.max(0.5, Math.min(4, newZoom));
        console.log(`[Preview Zoom] zoom=${newZoom.toFixed(2)}`);
        return newZoom;
      });
    };

    container.addEventListener('wheel', handlePreviewWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handlePreviewWheel);
    };
  }, [editorMode]);

  // Reset preview zoom when switching modes or products
  useEffect(() => {
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  }, [editorMode, product?.sku]);

  // Zoom functionality for slide panel 2D preview (zoom to cursor)
  const [slidePreviewTransform, setSlidePreviewTransform] = useState({ scale: 1, x: 0, y: 0 });

  useEffect(() => {
    const container = slidePreviewRef.current;
    if (!container || !slideOpen) return;

    const handleSlideWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const zoomSpeed = 0.1;

      // Get cursor position relative to container
      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      // Container center
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      setSlidePreviewTransform(prev => {
        const oldScale = prev.scale;
        let newScale = oldScale * (delta > 0 ? (1 - zoomSpeed) : (1 + zoomSpeed));
        newScale = Math.max(0.5, Math.min(4, newScale));

        // Calculate new position to zoom toward cursor
        const scaleRatio = newScale / oldScale;

        // Point under cursor in transformed space
        const pointX = (cursorX - centerX - prev.x) / oldScale;
        const pointY = (cursorY - centerY - prev.y) / oldScale;

        // New offset to keep cursor point stationary
        const newX = cursorX - centerX - pointX * newScale;
        const newY = cursorY - centerY - pointY * newScale;

        console.log(`[Slide Preview Zoom] scale=${newScale.toFixed(2)}, pos=(${newX.toFixed(0)}, ${newY.toFixed(0)})`);

        return { scale: newScale, x: newX, y: newY };
      });
    };

    container.addEventListener('wheel', handleSlideWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleSlideWheel);
    };
  }, [slideOpen]);

  // Reset slide preview zoom when panel closes or product changes
  useEffect(() => {
    setSlidePreviewTransform({ scale: 1, x: 0, y: 0 });
  }, [slideOpen, product?.sku]);

  // Zoom functionality for fullscreen 2D overlay
  useEffect(() => {
    const container = fullscreenContentRef.current;
    if (!container || !fullscreen2D) return;

    const handleFullscreenWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const zoomSpeed = 0.1;

      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      setFullscreenTransform(prev => {
        const oldScale = prev.scale;
        let newScale = oldScale * (delta > 0 ? (1 - zoomSpeed) : (1 + zoomSpeed));
        newScale = Math.max(0.5, Math.min(6, newScale));

        const pointX = (cursorX - centerX - prev.x) / oldScale;
        const pointY = (cursorY - centerY - prev.y) / oldScale;
        const newX = cursorX - centerX - pointX * newScale;
        const newY = cursorY - centerY - pointY * newScale;

        return { scale: newScale, x: newX, y: newY };
      });
    };

    container.addEventListener('wheel', handleFullscreenWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleFullscreenWheel);
    };
  }, [fullscreen2D]);

  // Reset fullscreen zoom when closing
  useEffect(() => {
    if (!fullscreen2D) {
      setFullscreenTransform({ scale: 1, x: 0, y: 0 });
    }
  }, [fullscreen2D]);

  // Update logo preview for slide panel - export only the logo objects (cropped)
  const updateLogoPreview = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();

    // Check if there are any non-print-area objects
    const contentObjects = objects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);

    if (contentObjects.length > 0) {
      // Hide print area objects temporarily for export
      const printAreaObjects = objects.filter((obj: fabric.Object) => (obj as any).isPrintArea);
      printAreaObjects.forEach((obj) => obj.set('visible', false));

      // Store original background and make it transparent for export
      const originalBg = canvas.backgroundColor;
      canvas.backgroundColor = 'transparent';

      // Calculate bounding box of all content objects
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      contentObjects.forEach((obj) => {
        const bounds = obj.getBoundingRect();
        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.left + bounds.width);
        maxY = Math.max(maxY, bounds.top + bounds.height);
      });

      // Calculate logo's relative position within print area
      const printBounds = (canvas as any).printAreaBounds;
      if (printBounds) {
        const logoWidth = maxX - minX;
        const logoHeight = maxY - minY;
        const logoCenterX = minX + logoWidth / 2;
        const logoCenterY = minY + logoHeight / 2;

        // Calculate relative position (0-1 within print area)
        const relX = (logoCenterX - printBounds.left) / printBounds.width;
        const relY = (logoCenterY - printBounds.top) / printBounds.height;
        const relScaleX = logoWidth / printBounds.width;
        const relScaleY = logoHeight / printBounds.height;

        setLogoRelativePos({
          x: Math.max(0, Math.min(1, relX)),
          y: Math.max(0, Math.min(1, relY)),
          scaleX: Math.min(1, relScaleX),
          scaleY: Math.min(1, relScaleY),
        });
      }

      // Add small padding
      const padding = 5;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(canvas.getWidth(), maxX + padding);
      maxY = Math.min(canvas.getHeight(), maxY + padding);

      // Export only the cropped area containing the logo
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2,
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
      });
      setLogoPreviewUrl(dataUrl);

      // Restore background and show print area objects again
      canvas.backgroundColor = originalBg;
      printAreaObjects.forEach((obj) => obj.set('visible', true));
      canvas.renderAll();
    } else {
      setLogoPreviewUrl(null);
    }
  }, []);

  // Keep ref in sync with callback so it can be called from earlier effects
  useEffect(() => {
    updateLogoPreviewRef.current = updateLogoPreview;
  }, [updateLogoPreview]);

  // Load logo onto canvas with proper scaling - fits within print area
  // If SVG, load as paths for bucket tool; if raster, load as image
  const loadLogoToCanvas = useCallback(async (imageUrl: string) => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    // Check if the logo is predominantly white and adjust canvas background
    const isWhiteLogo = await isImagePredominantlyWhite(imageUrl);
    if (isWhiteLogo) {
      canvas.setBackgroundColor('#e0e0e0', () => canvas.renderAll());
    } else {
      canvas.setBackgroundColor('#ffffff', () => canvas.renderAll());
    }

    // Get print area bounds if available
    const bounds = (canvas as any).printAreaBounds;
    let maxWidth: number;
    let maxHeight: number;
    let centerX: number;
    let centerY: number;

    if (bounds) {
      maxWidth = bounds.width;
      maxHeight = bounds.height;
      centerX = bounds.left + bounds.width / 2;
      centerY = bounds.top + bounds.height / 2;
    } else {
      const cWidth = canvas.getWidth();
      const cHeight = canvas.getHeight();
      maxWidth = cWidth * 0.5;
      maxHeight = cHeight * 0.5;
      centerX = cWidth / 2;
      centerY = cHeight / 2;
    }

    // Remove existing non-print-area objects
    const existingObjects = canvas.getObjects().filter((obj: fabric.Object) => !(obj as any).isPrintArea);
    existingObjects.forEach((obj) => canvas.remove(obj));

    // Check if it's an SVG - load as paths for bucket tool to work
    const isSvg = imageUrl.includes('data:image/svg+xml') || imageUrl.endsWith('.svg');

    if (isSvg) {
      // Load SVG as paths so bucket tool can change individual colors
      let svgString: string;
      if (imageUrl.startsWith('data:image/svg+xml;base64,')) {
        svgString = atob(imageUrl.split(',')[1]);
      } else if (imageUrl.startsWith('data:image/svg+xml,')) {
        svgString = decodeURIComponent(imageUrl.split(',')[1]);
      } else {
        // Fetch SVG from URL
        const response = await fetch(imageUrl);
        svgString = await response.text();
      }

      fabric.loadSVGFromString(svgString, (objects, options) => {
        if (!objects || objects.length === 0) {
          console.error('Failed to load SVG paths, falling back to image');
          // Fallback to image loading
          loadAsImage();
          return;
        }

        const svgGroup = fabric.util.groupSVGElements(objects, options);

        // Scale to fit print area
        const svgWidth = svgGroup.width || 100;
        const svgHeight = svgGroup.height || 100;
        const scaleX = maxWidth / svgWidth;
        const scaleY = maxHeight / svgHeight;
        const scale = Math.min(scaleX, scaleY, 1);

        svgGroup.set({
          scaleX: scale,
          scaleY: scale,
          left: centerX,
          top: centerY,
          originX: 'center',
          originY: 'center',
          angle: 0,
        });

        canvas.add(svgGroup);
        canvas.setActiveObject(svgGroup);
        canvas.renderAll();
        setHasLogo(true);
        console.log('Loaded SVG with', objects.length, 'paths - bucket tool ready');

        // Update preview
        updateLogoPreviewRef.current?.();
      });
      return;
    }

    // Load as image (for raster formats)
    function loadAsImage() {
      fabric.Image.fromURL(imageUrl, (img) => {
        if (!img || !img.width || !img.height) {
          console.error('Failed to load image');
          return;
        }

        const scaleX = maxWidth / img.width;
        const scaleY = maxHeight / img.height;
        const scale = Math.min(scaleX, scaleY, 1);

        img.set({
          scaleX: scale,
          scaleY: scale,
          left: centerX,
          top: centerY,
          originX: 'center',
          originY: 'center',
          angle: 0,
        });

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        setHasLogo(true);

        // Update preview
        updateLogoPreviewRef.current?.();
      }, { crossOrigin: 'anonymous' });
    }

    loadAsImage();
  }, [updateLogoPreview, currentTechnique]);

  // Auto-vectorize for Basic mode when L technique is selected
  const autoVectorizeForLaser = useCallback(async (base64Data: string): Promise<string> => {
    try {
      const response = await fetch('/api/logos/vectorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64Data,
          threshold: 50,
          turdsize: 2,
          alphamax: 1,
          colorMode: false,  // Monochrome for laser
          numberOfColors: 1,
          color: '#7A8288', // Stainless steel gray
        }),
      });
      const result = await response.json();
      if (result.success && result.data?.svg) {
        return result.data.svg;
      }
    } catch (error) {
      console.error('Auto-vectorize failed:', error);
    }
    return base64Data; // Return original if vectorization fails
  }, []);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    console.log('File upload started:', file.name, file.type);

    const ext = file.name.toLowerCase().split('.').pop();
    const isVectorFormat = ext === 'eps' || ext === 'ai';

    // For EPS/AI files, convert via backend first
    if (isVectorFormat) {
      console.log('Vector format detected, converting via backend...');
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/logos/convert-vector', {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();
        const convertedImage = result.data?.svg || result.data?.png;
        if (result.success && convertedImage) {
          console.log('Vector conversion successful, format:', result.data.convertedFormat, 'length:', convertedImage.length);
          setWizardLogoBase64(convertedImage);
          setWizardOriginalFormat(ext); // Show EPS/AI as original format in wizard
          setShowWizard(true);
        } else {
          console.error('Vector conversion failed:', result.error);
          alert('Kon bestand niet converteren. Probeer een ander formaat.');
        }
      } catch (error) {
        console.error('Error converting vector:', error);
        alert('Fout bij converteren van bestand.');
      }
      e.target.value = '';
      return;
    }

    // For regular images, read and auto-vectorize
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      console.log('File loaded, dataUrl length:', dataUrl?.length);

      // Store original raster for Vision color analysis (before vectorization converts it to SVG)
      setWizardOriginalBase64(dataUrl);

      // Auto-vectorize raster images so user can paint colors immediately
      console.log('Auto-vectorizing raster image...');
      setIsVectorizing(true);
      try {
        const response = await fetch('/api/logos/vectorize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Data: dataUrl,
            threshold: 50,
            turdsize: 2,
            alphamax: 1,
            colorMode: true,
            numberOfColors: 16,
          }),
        });
        const result = await response.json();
        setIsVectorizing(false);
        if (result.success && result.data?.svg) {
          console.log('Auto-vectorization successful, SVG ready for color editing');
          setWizardLogoBase64(result.data.svg);
          setWizardOriginalFormat(ext);
          setShowWizard(true);
        } else {
          // Fallback to original image if vectorization fails
          console.warn('Auto-vectorization failed, using original image:', result.error);
          setWizardLogoBase64(dataUrl);
          setWizardOriginalFormat(ext);
          setShowWizard(true);
        }
      } catch (error) {
        setIsVectorizing(false);
        console.error('Error auto-vectorizing:', error);
        // Fallback to original image
        setWizardLogoBase64(dataUrl);
        setWizardOriginalFormat(ext);
        setShowWizard(true);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // Handle wizard completion
  const handleWizardComplete = useCallback(async (result: {
    logoSrc: string;
    selectedTechnique: string;
    selectedColors: string;
    backgroundRemoved: boolean;
    detectedColors?: string[];
  }) => {
    console.log('Wizard complete:', result);

    // Close wizard
    setShowWizard(false);
    setWizardLogoBase64(null);
    setWizardOriginalFormat(undefined);
    setWizardOriginalBase64(null);

    // Set technique and colors from wizard result
    setSelectedTechnique(result.selectedTechnique);
    setSelectedColors(result.selectedColors);

    // Store detected colors for paint menu
    if (result.detectedColors && result.detectedColors.length > 0) {
      setPaletteColors(result.detectedColors);
      // Set the first color as the default bucket color
      setBucketColor(result.detectedColors[0]);
    }

    // Check if logo is predominantly white and adjust canvas background
    const isWhiteLogo = await isImagePredominantlyWhite(result.logoSrc);
    const newBackground = isWhiteLogo ? '#e0e0e0' : '#ffffff';
    setCanvasBackground(newBackground);

    // In basic mode, set the logo directly
    if (editorMode === 'basic') {
      setBasicLogoUrl(result.logoSrc);
      setLogoPreviewUrl(result.logoSrc);
      setHasLogo(true);
      setLogoRelativePos(prev => ({
        ...prev,
        scaleX: basicLogoSize / 100,
        scaleY: basicLogoSize / 100,
      }));
    } else {
      // In advanced mode, first draw print area with selected technique, then load logo
      const canvas = fabricCanvasRef.current;
      if (canvas && (canvas as any).drawPrintArea) {
        // Find the technique from the wizard result
        const wizardTechnique = techniques.find(t => t.code === result.selectedTechnique);
        if (wizardTechnique) {
          (canvas as any).drawPrintArea(wizardTechnique);
        }
      }
      // Small delay to ensure print area bounds are set before loading logo
      setTimeout(() => {
        loadLogoToCanvas(result.logoSrc);
      }, 50);
    }
  }, [editorMode, basicLogoSize, loadLogoToCanvas, techniques]);

  // Handle wizard cancel
  const handleWizardCancel = useCallback(() => {
    setShowWizard(false);
    setWizardLogoBase64(null);
    setWizardOriginalFormat(undefined);
    setWizardOriginalBase64(null);
  }, []);

  // Handle basic mode size slider change
  const handleBasicSizeChange = useCallback((newSize: number) => {
    setBasicLogoSize(newSize);
    setLogoRelativePos(prev => ({
      ...prev,
      scaleX: newSize / 100,
      scaleY: newSize / 100,
    }));
  }, []);

  // Handle basic mode logo drag
  const handleBasicLogoDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingBasicLogo(true);
    basicDragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      logoX: logoRelativePos.x,
      logoY: logoRelativePos.y,
    };
  }, [logoRelativePos]);

  const handleBasicLogoDragMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingBasicLogo || !basicDragStartRef.current || !basicPreviewWrapperRef.current || !currentTechnique || !product) return;

    const wrapper = basicPreviewWrapperRef.current;
    const rect = wrapper.getBoundingClientRect();

    // Calculate how much the mouse moved in pixels
    const deltaX = e.clientX - basicDragStartRef.current.mouseX;
    const deltaY = e.clientY - basicDragStartRef.current.mouseY;

    // Convert pixel movement to relative movement within print area
    // The wrapper shows the full product image, we need to scale the movement
    // Apply scale compensation for API coordinate mismatch
    const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
    const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
    const svgWidth = product.svgWidth || 1;
    const svgHeight = product.svgHeight || 1;

    // Scale factors: how many screen pixels per SVG pixel (accounting for zoom)
    const scaleX = (rect.width / svgWidth) * previewZoom;
    const scaleY = (rect.height / svgHeight) * previewZoom;

    // Convert screen pixel delta to relative position delta (0-1 range within print area)
    const relDeltaX = deltaX / (printAreaWidthPx * scaleX);
    const relDeltaY = deltaY / (printAreaHeightPx * scaleY);

    // Calculate new position, clamped to 0-1 range
    const newX = Math.max(0, Math.min(1, basicDragStartRef.current.logoX + relDeltaX));
    const newY = Math.max(0, Math.min(1, basicDragStartRef.current.logoY + relDeltaY));

    setLogoRelativePos(prev => ({
      ...prev,
      x: newX,
      y: newY,
    }));
  }, [isDraggingBasicLogo, currentTechnique, product, previewZoom]);

  const handleBasicLogoDragEnd = useCallback(() => {
    setIsDraggingBasicLogo(false);
    basicDragStartRef.current = null;
  }, []);

  // Add global mouse listeners for drag end (in case mouse leaves the element)
  useEffect(() => {
    if (isDraggingBasicLogo) {
      const handleMouseUp = () => handleBasicLogoDragEnd();
      const handleMouseMove = (e: MouseEvent) => {
        if (isDraggingBasicLogo && basicDragStartRef.current && basicPreviewWrapperRef.current && currentTechnique && product) {
          const wrapper = basicPreviewWrapperRef.current;
          const rect = wrapper.getBoundingClientRect();

          const deltaX = e.clientX - basicDragStartRef.current.mouseX;
          const deltaY = e.clientY - basicDragStartRef.current.mouseY;

          // Apply scale compensation for API coordinate mismatch
          const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
          const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
          const svgWidth = product.svgWidth || 1;
          const svgHeight = product.svgHeight || 1;

          const scaleX = (rect.width / svgWidth) * previewZoom;
          const scaleY = (rect.height / svgHeight) * previewZoom;

          const relDeltaX = deltaX / (printAreaWidthPx * scaleX);
          const relDeltaY = deltaY / (printAreaHeightPx * scaleY);

          const newX = Math.max(0, Math.min(1, basicDragStartRef.current.logoX + relDeltaX));
          const newY = Math.max(0, Math.min(1, basicDragStartRef.current.logoY + relDeltaY));

          setLogoRelativePos(prev => ({
            ...prev,
            x: newX,
            y: newY,
          }));
        }
      };

      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDraggingBasicLogo, handleBasicLogoDragEnd, currentTechnique, product, previewZoom]);

  // Transfer logo between Basic and Advanced mode when switching
  useEffect(() => {
    // When switching FROM advanced TO basic: save canvas state
    if (prevEditorModeRef.current === 'advanced' && editorMode === 'basic') {
      if (fabricCanvasRef.current) {
        const canvasJson = JSON.stringify(fabricCanvasRef.current.toJSON());
        setSavedCanvasJson(canvasJson);
      }
    }

    // When switching FROM basic TO advanced: restore canvas state
    if (prevEditorModeRef.current === 'basic' && editorMode === 'advanced') {
      const loadWhenReady = () => {
        if (fabricCanvasRef.current) {
          const canvas = fabricCanvasRef.current;
          // First try to restore saved canvas state
          if (savedCanvasJson) {
            canvas.loadFromJSON(savedCanvasJson, () => {
              canvas.renderAll();
              setTimeout(() => updateLogoPreview(), 100);
            });
          } else if (basicLogoUrl) {
            // Fallback: load basicLogoUrl if no saved state
            loadLogoToCanvas(basicLogoUrl);
          }
        } else {
          setTimeout(loadWhenReady, 100);
        }
      };
      setTimeout(loadWhenReady, 300);
    }
    // Update the ref to track current mode for next change
    prevEditorModeRef.current = editorMode;
  }, [editorMode, basicLogoUrl, savedCanvasJson, loadLogoToCanvas, updateLogoPreview]);

  const handleClear = useCallback(() => {
    // Reset canvas background to white when clearing
    setCanvasBackground('#ffffff');

    if (editorMode === 'basic') {
      setBasicLogoUrl(null);
      setLogoPreviewUrl(null);
      setHasLogo(false);
    } else if (fabricCanvasRef.current) {
      fabricCanvasRef.current.clear();
      fabricCanvasRef.current.setBackgroundColor('#ffffff', () => {});
      fabricCanvasRef.current.renderAll();
      setHasLogo(false);
    }
  }, [editorMode]);

  // Load recent logos from API (filtered by client fingerprint)
  const loadRecentLogos = useCallback(async () => {
    setIsLoadingRecent(true);
    try {
      const fingerprint = getClientFingerprint();
      const response = await fetch(`${window.location.origin}/api/logos/recent?limit=10&fingerprint=${encodeURIComponent(fingerprint)}`);
      const data = await response.json();
      if (data.success && data.data) {
        // Add thumbnail URL based on file path (API returns processedPath)
        const logosWithThumbnails = data.data.map((logo: any) => ({
          ...logo,
          filePath: logo.processedPath || logo.filePath,
          thumbnailUrl: `${window.location.origin}/api/logos/base64?path=${encodeURIComponent(logo.processedPath || logo.filePath)}`
        }));
        setRecentLogos(logosWithThumbnails);
      }
    } catch (error) {
      console.error('Error loading recent logos:', error);
    } finally {
      setIsLoadingRecent(false);
    }
  }, []);

  // Toggle recent panel and load logos
  const handleRecentClick = useCallback(() => {
    if (!showRecentPanel) {
      loadRecentLogos();
    }
    setShowRecentPanel(!showRecentPanel);
  }, [showRecentPanel, loadRecentLogos]);

  // Select a recent logo and load it
  const handleSelectRecentLogo = useCallback(async (logo: { filePath: string; originalFilename: string }) => {
    try {
      const response = await fetch(`${window.location.origin}/api/logos/base64?path=${encodeURIComponent(logo.filePath)}`);
      const data = await response.json();
      if (data.success && data.data?.base64) {
        const base64Url = data.data.base64.startsWith('data:') ? data.data.base64 : `data:image/png;base64,${data.data.base64}`;

        // Check if logo is predominantly white and adjust canvas background
        const isWhiteLogo = await isImagePredominantlyWhite(base64Url);
        const newBackground = isWhiteLogo ? '#e0e0e0' : '#ffffff';
        setCanvasBackground(newBackground);

        if (editorMode === 'basic') {
          setBasicLogoUrl(base64Url);
          setLogoPreviewUrl(base64Url);
          setHasLogo(true);
        } else {
          // Load into fabric canvas
          loadLogoToCanvas(base64Url);
        }
        setShowRecentPanel(false);
      }
    } catch (error) {
      console.error('Error loading recent logo:', error);
    }
  }, [editorMode, loadLogoToCanvas]);

  const handleReset = useCallback(() => {
    if (fabricCanvasRef.current) {
      // Remove last added object
      const objects = fabricCanvasRef.current.getObjects();
      if (objects.length > 0) {
        fabricCanvasRef.current.remove(objects[objects.length - 1]);
        fabricCanvasRef.current.renderAll();
      }
    }
  }, []);

  const handleRotate = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      activeObject.rotate((activeObject.angle || 0) + 90);
      fabricCanvasRef.current.renderAll();
    }
  }, []);

  const handleFlipH = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      activeObject.set('flipX', !activeObject.flipX);
      fabricCanvasRef.current.renderAll();
    }
  }, []);

  const handleFlipV = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      activeObject.set('flipY', !activeObject.flipY);
      fabricCanvasRef.current.renderAll();
    }
  }, []);

  const handleDelete = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      fabricCanvasRef.current.remove(activeObject);
      fabricCanvasRef.current.renderAll();
      if (fabricCanvasRef.current.getObjects().length === 0) {
        setHasLogo(false);
      }
    }
  }, []);

  const handleSave = useCallback(() => {
    if (!fabricCanvasRef.current || !onSave) return;

    const canvas = fabricCanvasRef.current;

    // Get SVG export (excluding print area objects)
    const objects = canvas.getObjects();
    const contentObjects = objects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);
    const printAreaObjects = objects.filter((obj: fabric.Object) => (obj as any).isPrintArea);

    // Hide print area for SVG export
    printAreaObjects.forEach((obj) => obj.set('visible', false));
    canvas.renderAll();

    // Calculate bounding box for content
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    contentObjects.forEach((obj) => {
      const bounds = obj.getBoundingRect();
      minX = Math.min(minX, bounds.left);
      minY = Math.min(minY, bounds.top);
      maxX = Math.max(maxX, bounds.left + bounds.width);
      maxY = Math.max(maxY, bounds.top + bounds.height);
    });

    // Generate SVG with viewBox set to content area
    const padding = 5;
    const svgWidth = maxX - minX + padding * 2;
    const svgHeight = maxY - minY + padding * 2;
    const svgData = contentObjects.length > 0 ? canvas.toSVG({
      viewBox: {
        x: minX - padding,
        y: minY - padding,
        width: svgWidth,
        height: svgHeight,
      },
      width: String(svgWidth),
      height: String(svgHeight),
    }) : undefined;

    // Restore print area visibility
    printAreaObjects.forEach((obj) => obj.set('visible', true));
    canvas.renderAll();

    const imageData = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 2,
    });

    const logoData: LogoData = {
      imageData: imageData,
      svgData: svgData,
      width: canvas.getWidth(),
      height: canvas.getHeight(),
      technique: selectedTechnique,
      colors: selectedColors === 'full' ? -1 : parseInt(selectedColors),
    };

    // Save to cache for "Recent" logos - use the logo preview (not the canvas with background)
    const logoToCache = editorMode === 'basic' ? basicLogoUrl : logoPreviewUrl;
    if (logoToCache) {
      try {
        fetch(`${window.location.origin}/api/logos/cache`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Data: logoToCache,
            filename: `logo_${Date.now()}.png`,
            mimeType: 'image/png',
            clientFingerprint: getClientFingerprint(),
          }),
        }).catch((err) => console.warn('Failed to cache logo:', err));
      } catch (err) {
        console.warn('Failed to cache logo:', err);
      }
    }

    onSave(logoData);
  }, [onSave, selectedTechnique, selectedColors, editorMode, basicLogoUrl, logoPreviewUrl]);

  // Add to Order - create transaction and notify parent window
  const handleAddToOrder = useCallback(async () => {
    // Check if logo exists
    const logoUrl = editorMode === 'basic' ? basicLogoUrl : logoPreviewUrl;
    if (!logoUrl && !hasLogo) {
      alert(t('noLogoForOrder'));
      return;
    }

    setAddToOrderStatus('loading');

    try {
      // Get basketId from props, URL params, or generate one
      const urlParams = new URLSearchParams(window.location.search);
      const basketId = basketIdProp || urlParams.get('basket_id') || urlParams.get('basketId') || crypto.randomUUID();
      const customerId = customerIdProp || urlParams.get('customer_id') || urlParams.get('customerId') || '';

      // Get technique info
      const selectedTech = techniques.find(tech => tech.code === selectedTechnique);

      // Prepare logo config
      const logoConfig = {
        logoUrl: logoUrl,
        techniqueCode: selectedTechnique,
        techniqueName: selectedTech?.name || selectedTechnique,
        position: selectedTech?.position || 'midden',
        printArea: selectedTech?.dimensions || '',
        colors: selectedColors,
        scale: basicLogoSize,
        editorMode,
      };

      // Create composite screenshot (product + logo) for preview
      let screenshotDataUrl = '';
      const displayLogoUrl = editorMode === 'basic' ? basicLogoUrl : logoPreviewUrl;

      // Try to create composite screenshot with product + logo
      if (product?.imageUrl && displayLogoUrl && currentTechnique) {
        try {
          console.log('[AddToOrder] Creating composite screenshot');

          // Create canvas for rendering
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Failed to get canvas context');

          // Set canvas size (use product SVG dimensions)
          const canvasWidth = product.svgWidth || 800;
          const canvasHeight = product.svgHeight || 800;
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          // Fill white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvasWidth, canvasHeight);

          // Fetch SVG content and convert to blob URL to avoid CORS issues
          const svgResponse = await fetch(product.imageUrl);
          const svgText = await svgResponse.text();
          const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
          const svgBlobUrl = URL.createObjectURL(svgBlob);

          // Load product image from blob URL
          const productImg = new Image();
          await new Promise<void>((resolve, reject) => {
            productImg.onload = () => resolve();
            productImg.onerror = (e) => reject(new Error('Failed to load product image'));
            productImg.src = svgBlobUrl;
          });

          // Draw product image
          ctx.drawImage(productImg, 0, 0, canvasWidth, canvasHeight);
          URL.revokeObjectURL(svgBlobUrl);

          // Calculate logo position (apply scale compensation for API coordinate mismatch)
          const printCenterX = currentTechnique.centroidX || canvasWidth / 2;
          const printCenterY = currentTechnique.centroidY || canvasHeight / 2;
          const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
          const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
          const printLeftPx = printCenterX - printAreaWidthPx / 2;
          const printTopPx = printCenterY - printAreaHeightPx / 2;
          const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
          const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);

          // Check technique type for effects
          const isLaser = currentTechnique?.code?.startsWith('L');
          const logoDisplayUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : displayLogoUrl;

          // Load logo image
          const logoImg = new Image();
          await new Promise<void>((resolve, reject) => {
            logoImg.onload = () => resolve();
            logoImg.onerror = (e) => reject(new Error('Failed to load logo image'));
            logoImg.src = logoDisplayUrl;
          });

          // Calculate logo dimensions preserving aspect ratio
          const logoNaturalWidth = logoImg.naturalWidth || logoImg.width || 100;
          const logoNaturalHeight = logoImg.naturalHeight || logoImg.height || 100;
          const logoAspectRatio = logoNaturalWidth / logoNaturalHeight;

          // Maximum bounds based on scale
          const maxLogoWidth = logoRelativePos.scaleX * printAreaWidthPx;
          const maxLogoHeight = logoRelativePos.scaleY * printAreaHeightPx;

          // Fit logo within bounds while preserving aspect ratio
          let finalLogoWidth: number, finalLogoHeight: number;
          if (maxLogoWidth / maxLogoHeight > logoAspectRatio) {
            finalLogoHeight = maxLogoHeight;
            finalLogoWidth = finalLogoHeight * logoAspectRatio;
          } else {
            finalLogoWidth = maxLogoWidth;
            finalLogoHeight = finalLogoWidth / logoAspectRatio;
          }

          // Calculate logo draw position (centered on logoCenterX/Y)
          const logoDrawX = logoCenterXPx - finalLogoWidth / 2;
          const logoDrawY = logoCenterYPx - finalLogoHeight / 2;

          // Draw logo with correct aspect ratio
          ctx.drawImage(logoImg, logoDrawX, logoDrawY, finalLogoWidth, finalLogoHeight);

          // Convert canvas to data URL
          screenshotDataUrl = canvas.toDataURL('image/png');
          console.log('[AddToOrder] Composite screenshot created successfully');
        } catch (screenshotError) {
          console.warn('[AddToOrder] Failed to create composite screenshot, falling back to logo only:', screenshotError);
          // Fallback to just logo
          screenshotDataUrl = displayLogoUrl || '';
        }
      } else {
        // Fallback to just logo if product/technique not available
        screenshotDataUrl = displayLogoUrl || '';
      }

      // Export logo as SVG for vector preservation
      let logoSvgData = '';
      if (fabricCanvasRef.current) {
        const canvas = fabricCanvasRef.current;
        const objects = canvas.getObjects();
        const contentObjects = objects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);
        if (contentObjects.length > 0) {
          const logoObj = contentObjects[0];
          if (logoObj.type === 'group') {
            const group = logoObj as fabric.Group;
            const groupBounds = group.getBoundingRect();
            const width = groupBounds.width;
            const height = groupBounds.height;
            const clonedGroup = fabric.util.object.clone(group);
            clonedGroup.set({ left: width / 2, top: height / 2, originX: 'center', originY: 'center' });
            const tempCanvas = new fabric.StaticCanvas(null, { width, height });
            tempCanvas.add(clonedGroup);
            logoSvgData = tempCanvas.toSVG();
            tempCanvas.dispose();
          } else {
            const bounds = logoObj.getBoundingRect();
            const width = bounds.width + 10;
            const height = bounds.height + 10;
            const tempCanvas = new fabric.StaticCanvas(null, { width, height });
            const clonedObj = fabric.util.object.clone(logoObj);
            clonedObj.set({ left: width / 2, top: height / 2, originX: 'center', originY: 'center' });
            tempCanvas.add(clonedObj);
            logoSvgData = tempCanvas.toSVG();
            tempCanvas.dispose();
          }
          // Convert to base64 data URL
          logoSvgData = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(logoSvgData)));
        }
      }

      // Add SVG to logo config for vector preservation
      if (logoSvgData) {
        logoConfig.logoUrl = logoSvgData;
      }

      // Call transaction API
      const response = await fetch('/api/transactions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          basketId,
          customerId,
          productSku: product?.sku || '',
          variantSku: product?.sku,
          techniqueCode: selectedTechnique,
          techniqueName: selectedTech?.name,
          printArea: selectedTech?.dimensions,
          positionName: selectedTech?.position,
          quantity: quantityProp,
          logoConfig,
          screenshotDataUrl,
          sourceSystem: 'brinxx',
          callbackUrl,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create transaction');
      }

      const data = await response.json();
      const transactionId = data.transaction_id;
      setLastTransactionId(transactionId);
      setAddToOrderStatus('success');

      // Show files modal with download links
      setFilesModalData({
        transactionId,
        productSku: product?.sku || '',
        techniqueName: selectedTech?.name || selectedTechnique,
        quantity: quantityProp || 1,
        files: {
          screenshot: `/api/transactions/${transactionId}/screenshot`,
          screenshot_hq: `/api/transactions/${transactionId}/screenshot?quality=high`,
          logo_original: `/api/transactions/${transactionId}/logo`,
          pdf: `/api/transactions/${transactionId}/pdf`,
        },
      });
      setShowFilesModal(true);

      // Send message to parent window (for iframe integration)
      // SECURITY: Use document.referrer origin or fallback to '*' only if no referrer
      if (window.parent !== window) {
        let targetOrigin = '*';
        try {
          if (document.referrer) {
            targetOrigin = new URL(document.referrer).origin;
          }
        } catch (e) {
          console.warn('[postMessage] Could not parse referrer, using wildcard');
        }
        window.parent.postMessage({
          type: 'MAGIC_LOGO_ADD_TO_ORDER',
          transactionId,
          basketId,
          customerId,
          productSku: product?.sku,
          quantity: quantityProp,
          techniqueCode: selectedTechnique,
          techniqueName: selectedTech?.name,
          logoConfig,
          filesUrl: `/api/transactions/${transactionId}/files`,
        }, targetOrigin);

        // Don't auto-close, let user close via files modal
      }

      // Fire callback URL if provided
      if (callbackUrl) {
        try {
          await fetch(callbackUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            mode: 'no-cors',
            body: JSON.stringify({
              transactionId,
              productSku: product?.sku || '',
              variantSku: product?.sku || '',
              quantity: quantityProp,
              techniqueCode: selectedTechnique,
              techniqueName: selectedTech?.name || '',
              printArea: selectedTech?.dimensions || '',
              filesUrl: `/api/transactions/${transactionId}/files`,
              screenshotUrl: `/api/transactions/${transactionId}/screenshot`,
              logoUrl: `/api/transactions/${transactionId}/logo`,
            }),
          });
          console.log('[handleAddToOrder] Callback fired to:', callbackUrl);
        } catch (cbError) {
          console.error('[handleAddToOrder] Callback error:', cbError);
        }
      }

      // Reset status after 3 seconds
      setTimeout(() => setAddToOrderStatus('idle'), 3000);

    } catch (error) {
      console.error('Add to order error:', error);
      setAddToOrderStatus('error');
      setTimeout(() => setAddToOrderStatus('idle'), 3000);
    }
  }, [product, editorMode, basicLogoUrl, logoPreviewUrl, hasLogo, selectedTechnique, selectedColors, basicLogoSize, techniques, t, quantityProp, callbackUrl, customerIdProp, basketIdProp, onClose, currentTechnique, logoRelativePos, laserProcessedLogoUrl]);

  // Copy artwork (product + logo) as PNG to clipboard
  const handleCopyLogo = useCallback(async () => {
    console.log('[CopyArtwork] Starting...', { editorMode, hasProduct: !!product?.imageUrl, hasLogo: !!logoPreviewUrl, hasTechnique: !!currentTechnique });

    // For basic mode, render product SVG + logo on canvas
    if (editorMode === 'basic' && product?.imageUrl && logoPreviewUrl && currentTechnique) {
      try {
        console.log('[CopyArtwork] Basic mode - creating canvas artwork');

        // Create canvas for rendering
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Set canvas size (use product SVG dimensions)
        const canvasWidth = product.svgWidth || 800;
        const canvasHeight = product.svgHeight || 800;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        console.log('[CopyArtwork] Canvas size:', canvasWidth, 'x', canvasHeight);

        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Fetch SVG content and convert to blob URL to avoid CORS issues
        console.log('[CopyArtwork] Fetching product SVG:', product.imageUrl);
        const svgResponse = await fetch(product.imageUrl);
        const svgText = await svgResponse.text();
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
        const svgBlobUrl = URL.createObjectURL(svgBlob);

        // Load product image from blob URL
        const productImg = new Image();

        await new Promise<void>((resolve, reject) => {
          productImg.onload = () => {
            console.log('[CopyArtwork] Product image loaded');
            resolve();
          };
          productImg.onerror = (e) => {
            console.error('[CopyArtwork] Product image failed to load:', e);
            reject(new Error('Failed to load product image'));
          };
          productImg.src = svgBlobUrl;
        });

        // Draw product image
        ctx.drawImage(productImg, 0, 0, canvasWidth, canvasHeight);
        URL.revokeObjectURL(svgBlobUrl); // Clean up

        // Calculate logo position (apply scale compensation for API coordinate mismatch)
        const printCenterX = currentTechnique.centroidX || canvasWidth / 2;
        const printCenterY = currentTechnique.centroidY || canvasHeight / 2;
        const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printLeftPx = printCenterX - printAreaWidthPx / 2;
        const printTopPx = printCenterY - printAreaHeightPx / 2;
        const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
        const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);
        const logoWidthPx = logoRelativePos.scaleX * printAreaWidthPx;
        const logoHeightPx = logoRelativePos.scaleY * printAreaHeightPx;
        console.log('[CopyArtwork] Logo position:', { logoCenterXPx, logoCenterYPx, logoWidthPx, logoHeightPx });

        // Check technique type for effects
        const isLaser = currentTechnique?.code?.startsWith('L');
        const displayLogoUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : logoPreviewUrl;

        // Load logo image
        const logoImg = new Image();

        await new Promise<void>((resolve, reject) => {
          logoImg.onload = () => {
            console.log('[CopyArtwork] Logo image loaded, natural size:', logoImg.naturalWidth, 'x', logoImg.naturalHeight);
            resolve();
          };
          logoImg.onerror = (e) => {
            console.error('[CopyArtwork] Logo image failed to load:', e);
            reject(new Error('Failed to load logo image'));
          };
          logoImg.src = displayLogoUrl;
        });

        // Calculate logo dimensions preserving aspect ratio
        const logoNaturalWidth = logoImg.naturalWidth || logoImg.width || 100;
        const logoNaturalHeight = logoImg.naturalHeight || logoImg.height || 100;
        const logoAspectRatio = logoNaturalWidth / logoNaturalHeight;

        // Maximum bounds based on scale
        const maxLogoWidth = logoRelativePos.scaleX * printAreaWidthPx;
        const maxLogoHeight = logoRelativePos.scaleY * printAreaHeightPx;

        // Fit logo within bounds while preserving aspect ratio
        let finalLogoWidth: number, finalLogoHeight: number;
        if (maxLogoWidth / maxLogoHeight > logoAspectRatio) {
          // Height is the limiting factor
          finalLogoHeight = maxLogoHeight;
          finalLogoWidth = finalLogoHeight * logoAspectRatio;
        } else {
          // Width is the limiting factor
          finalLogoWidth = maxLogoWidth;
          finalLogoHeight = finalLogoWidth / logoAspectRatio;
        }
        console.log('[CopyArtwork] Logo size adjusted for aspect ratio:', finalLogoWidth, 'x', finalLogoHeight);

        // Calculate logo draw position (centered on logoCenterX/Y)
        const logoDrawX = logoCenterXPx - finalLogoWidth / 2;
        const logoDrawY = logoCenterYPx - finalLogoHeight / 2;

        // Draw logo with correct aspect ratio
        ctx.drawImage(logoImg, logoDrawX, logoDrawY, finalLogoWidth, finalLogoHeight);
        console.log('[CopyArtwork] Drew logo at:', logoDrawX, logoDrawY, 'size:', finalLogoWidth, 'x', finalLogoHeight);

        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          }, 'image/png');
        });

        console.log('[CopyArtwork] Created blob, copying to clipboard...');

        // Copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob,
          }),
        ]);

        console.log('[CopyArtwork] Success!');
        // Show feedback
        setCopyFeedback('logo');
        setTimeout(() => setCopyFeedback(null), 2000);
        return; // Important: return here to prevent fallback
      } catch (error) {
        console.error('[CopyArtwork] Error:', error);
        // Don't fall through to fallback - show error
        alert(t('copyError'));
        return;
      }
    }

    // For advanced mode, also render product SVG + logo on canvas
    if (editorMode === 'advanced' && product?.imageUrl && logoPreviewUrl && currentTechnique) {
      console.log('[CopyArtwork] Advanced mode - creating canvas artwork');
      try {
        // Create canvas for rendering
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Set canvas size (use product SVG dimensions)
        const canvasWidth = product.svgWidth || 800;
        const canvasHeight = product.svgHeight || 800;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        console.log('[CopyArtwork] Canvas size:', canvasWidth, 'x', canvasHeight);

        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Fetch SVG content and convert to blob URL to avoid CORS issues
        console.log('[CopyArtwork] Fetching product SVG:', product.imageUrl);
        const svgResponse = await fetch(product.imageUrl);
        const svgText = await svgResponse.text();
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
        const svgBlobUrl = URL.createObjectURL(svgBlob);

        // Load product image from blob URL
        const productImg = new Image();

        await new Promise<void>((resolve, reject) => {
          productImg.onload = () => {
            console.log('[CopyArtwork] Product image loaded');
            resolve();
          };
          productImg.onerror = (e) => {
            console.error('[CopyArtwork] Product image failed to load:', e);
            reject(new Error('Failed to load product image'));
          };
          productImg.src = svgBlobUrl;
        });

        // Draw product image
        ctx.drawImage(productImg, 0, 0, canvasWidth, canvasHeight);
        URL.revokeObjectURL(svgBlobUrl); // Clean up

        // Calculate logo position (apply scale compensation for API coordinate mismatch)
        const printCenterX = currentTechnique.centroidX || canvasWidth / 2;
        const printCenterY = currentTechnique.centroidY || canvasHeight / 2;
        const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printLeftPx = printCenterX - printAreaWidthPx / 2;
        const printTopPx = printCenterY - printAreaHeightPx / 2;
        const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
        const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);
        const logoWidthPx = logoRelativePos.scaleX * printAreaWidthPx;
        const logoHeightPx = logoRelativePos.scaleY * printAreaHeightPx;
        console.log('[CopyArtwork] Logo position:', { logoCenterXPx, logoCenterYPx, logoWidthPx, logoHeightPx });

        // Check technique type for effects
        const isLaser = currentTechnique?.code?.startsWith('L');
        const displayLogoUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : logoPreviewUrl;

        // Load logo image
        const logoImg = new Image();

        await new Promise<void>((resolve, reject) => {
          logoImg.onload = () => {
            console.log('[CopyArtwork] Logo image loaded, natural size:', logoImg.naturalWidth, 'x', logoImg.naturalHeight);
            resolve();
          };
          logoImg.onerror = (e) => {
            console.error('[CopyArtwork] Logo image failed to load:', e);
            reject(new Error('Failed to load logo image'));
          };
          logoImg.src = displayLogoUrl;
        });

        // Calculate logo dimensions preserving aspect ratio
        const logoNaturalWidth = logoImg.naturalWidth || logoImg.width || 100;
        const logoNaturalHeight = logoImg.naturalHeight || logoImg.height || 100;
        const logoAspectRatio = logoNaturalWidth / logoNaturalHeight;

        // Maximum bounds based on scale
        const maxLogoWidth = logoRelativePos.scaleX * printAreaWidthPx;
        const maxLogoHeight = logoRelativePos.scaleY * printAreaHeightPx;

        // Fit logo within bounds while preserving aspect ratio
        let finalLogoWidth: number, finalLogoHeight: number;
        if (maxLogoWidth / maxLogoHeight > logoAspectRatio) {
          // Height is the limiting factor
          finalLogoHeight = maxLogoHeight;
          finalLogoWidth = finalLogoHeight * logoAspectRatio;
        } else {
          // Width is the limiting factor
          finalLogoWidth = maxLogoWidth;
          finalLogoHeight = finalLogoWidth / logoAspectRatio;
        }
        console.log('[CopyArtwork] Logo size adjusted for aspect ratio:', finalLogoWidth, 'x', finalLogoHeight);

        // Calculate logo draw position (centered on logoCenterX/Y)
        const logoDrawX = logoCenterXPx - finalLogoWidth / 2;
        const logoDrawY = logoCenterYPx - finalLogoHeight / 2;

        // Draw logo with correct aspect ratio
        ctx.drawImage(logoImg, logoDrawX, logoDrawY, finalLogoWidth, finalLogoHeight);
        console.log('[CopyArtwork] Drew logo at:', logoDrawX, logoDrawY, 'size:', finalLogoWidth, 'x', finalLogoHeight);

        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          }, 'image/png');
        });

        console.log('[CopyArtwork] Created blob, copying to clipboard...');

        // Copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob,
          }),
        ]);

        console.log('[CopyArtwork] Success!');
        // Show feedback
        setCopyFeedback('logo');
        setTimeout(() => setCopyFeedback(null), 2000);
        return;
      } catch (error) {
        console.error('[CopyArtwork] Error:', error);
        alert(t('copyError'));
        return;
      }
    }

    console.log('[CopyArtwork] Using fallback (logo only) - missing requirements');
    // Fallback - copy just the logo if product/technique not available
    const logoUrl = editorMode === 'basic' ? basicLogoUrl : logoPreviewUrl;

    if (!logoUrl) {
      alert(t('noLogoToCopy'));
      return;
    }

    try {
      // Convert data URL to blob
      const response = await fetch(logoUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);

      // Show feedback
      setCopyFeedback('logo');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Copy logo error:', error);
      alert(t('copyError'));
    }
  }, [editorMode, basicLogoUrl, logoPreviewUrl, product, currentTechnique, logoRelativePos, laserProcessedLogoUrl, t]);

  // Generate and download PDF (Korrekturabzug)
  const handleDownloadPdf = useCallback(async () => {
    if (!product?.imageUrl || (!logoPreviewUrl && !basicLogoUrl)) {
      alert(t('noLogoToCopy'));
      return;
    }

    setIsGeneratingPdf(true);

    try {
      // Create composite image (product + logo) similar to handleCopyLogo
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      const canvasWidth = product.svgWidth || 800;
      const canvasHeight = product.svgHeight || 800;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Fetch and draw product SVG
      const svgResponse = await fetch(product.imageUrl);
      const svgText = await svgResponse.text();
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const svgBlobUrl = URL.createObjectURL(svgBlob);

      const productImg = new Image();
      await new Promise<void>((resolve, reject) => {
        productImg.onload = () => resolve();
        productImg.onerror = (e) => reject(new Error('Failed to load product image'));
        productImg.src = svgBlobUrl;
      });
      ctx.drawImage(productImg, 0, 0, canvasWidth, canvasHeight);
      URL.revokeObjectURL(svgBlobUrl);

      // Draw logo on product (apply scale compensation for API coordinate mismatch)
      if (currentTechnique) {
        const printCenterX = currentTechnique.centroidX || canvasWidth / 2;
        const printCenterY = currentTechnique.centroidY || canvasHeight / 2;
        const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
        const printLeftPx = printCenterX - printAreaWidthPx / 2;
        const printTopPx = printCenterY - printAreaHeightPx / 2;
        const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
        const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);

        const isLaser = currentTechnique?.code?.startsWith('L');
        const displayLogoUrl = editorMode === 'basic' ? basicLogoUrl : logoPreviewUrl;
        const logoDisplayUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : displayLogoUrl;

        if (logoDisplayUrl) {
          const logoImg = new Image();
          await new Promise<void>((resolve, reject) => {
            logoImg.onload = () => resolve();
            logoImg.onerror = (e) => reject(new Error('Failed to load logo'));
            logoImg.src = logoDisplayUrl;
          });

          const logoNaturalWidth = logoImg.naturalWidth || logoImg.width || 100;
          const logoNaturalHeight = logoImg.naturalHeight || logoImg.height || 100;
          const logoAspectRatio = logoNaturalWidth / logoNaturalHeight;

          const maxLogoWidth = logoRelativePos.scaleX * printAreaWidthPx;
          const maxLogoHeight = logoRelativePos.scaleY * printAreaHeightPx;

          let finalLogoWidth: number, finalLogoHeight: number;
          if (maxLogoWidth / maxLogoHeight > logoAspectRatio) {
            finalLogoHeight = maxLogoHeight;
            finalLogoWidth = finalLogoHeight * logoAspectRatio;
          } else {
            finalLogoWidth = maxLogoWidth;
            finalLogoHeight = finalLogoWidth / logoAspectRatio;
          }

          const logoDrawX = logoCenterXPx - finalLogoWidth / 2;
          const logoDrawY = logoCenterYPx - finalLogoHeight / 2;
          ctx.drawImage(logoImg, logoDrawX, logoDrawY, finalLogoWidth, finalLogoHeight);
        }
      }

      // Convert to data URL for PDF
      const screenshotDataUrl = canvas.toDataURL('image/png');

      // Create a simple PDF with the image using browser
      // For now, open the image in a new tab for printing/saving as PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Korrekturabzug - ${product.sku}</title>
            <style>
              @page { size: A4; margin: 20mm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .header { font-size: 18px; font-weight: bold; font-style: italic; text-decoration: underline; margin-bottom: 10px; }
              .article { font-size: 14px; font-weight: bold; margin-bottom: 5px; }
              .technique { font-size: 12px; margin-bottom: 20px; }
              .image-container { text-align: center; margin: 20px 0; }
              .image-container img { max-width: 300px; max-height: 300px; }
              .label { font-size: 10px; margin-bottom: 10px; }
              .footer { font-size: 10px; color: #666; margin-top: 20px; text-align: right; }
              @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            <div class="header">Korrekturabzug</div>
            <div class="article">Artikel ${product.sku}</div>
            <div class="technique">${currentTechnique?.name || ''} ${currentTechnique?.position ? 'op ' + currentTechnique.position : ''}: ${currentTechnique?.dimensions || ''}</div>
            <div class="label">Darstellung 1:1</div>
            <div class="image-container">
              <img src="${screenshotDataUrl}" alt="Product met logo" />
            </div>
            <div class="footer">
              Datum: ${new Date().toLocaleDateString('nl-NL')}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
              };
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
      }

      setCopyFeedback('pdf');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('[PDF] Error:', error);
      alert(t('copyError'));
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [editorMode, basicLogoUrl, logoPreviewUrl, product, currentTechnique, logoRelativePos, laserProcessedLogoUrl, t]);

  // Copy logo as SVG to clipboard
  const handleCopySvg = useCallback(async () => {
    if (!fabricCanvasRef.current && editorMode === 'advanced') {
      alert(t('noLogoToCopy'));
      return;
    }

    // Check if we have a logo
    const hasLogoToExport = editorMode === 'basic' ? !!basicLogoUrl : hasLogo;
    if (!hasLogoToExport) {
      alert(t('noLogoToCopy'));
      return;
    }

    try {
      let svgString: string;

      if (editorMode === 'advanced' && fabricCanvasRef.current) {
        // For advanced mode, export from fabric canvas
        const canvas = fabricCanvasRef.current;
        const objects = canvas.getObjects();
        const contentObjects = objects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);

        if (contentObjects.length === 0) {
          alert(t('noLogoToCopy'));
          return;
        }

        // Hide print area objects temporarily
        const printAreaObjects = objects.filter((obj: fabric.Object) => (obj as any).isPrintArea);
        printAreaObjects.forEach((obj) => obj.set('visible', false));

        // Calculate bounding box
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        contentObjects.forEach((obj) => {
          const bounds = obj.getBoundingRect();
          minX = Math.min(minX, bounds.left);
          minY = Math.min(minY, bounds.top);
          maxX = Math.max(maxX, bounds.left + bounds.width);
          maxY = Math.max(maxY, bounds.top + bounds.height);
        });

        // Export as SVG with viewBox set to content area
        const padding = 5;
        const width = maxX - minX + padding * 2;
        const height = maxY - minY + padding * 2;

        svgString = canvas.toSVG({
          viewBox: {
            x: minX - padding,
            y: minY - padding,
            width: width,
            height: height,
          },
          width: String(width),
          height: String(height),
        });

        // Restore print area objects
        printAreaObjects.forEach((obj) => obj.set('visible', true));
        canvas.renderAll();
      } else {
        // For basic mode, create a simple SVG wrapper around the image
        const logoUrl = basicLogoUrl || logoPreviewUrl;
        if (!logoUrl) {
          alert(t('noLogoToCopy'));
          return;
        }

        // Create SVG with embedded image
        svgString = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 200 200">
  <image xlink:href="${logoUrl}" x="0" y="0" width="200" height="200" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(svgString);

      // Show feedback
      setCopyFeedback('svg');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Copy SVG error:', error);
      alert(t('copyError'));
    }
  }, [editorMode, basicLogoUrl, logoPreviewUrl, hasLogo, t]);

  // Copy 3D screenshot to clipboard
  const handleCopy3D = useCallback(async () => {
    if (!product?.has3DModel) {
      alert(t('no3DModel'));
      return;
    }

    if (!iframe3DRef.current?.contentWindow) {
      alert(t('no3DModel'));
      return;
    }

    setIsCopying3D(true);

    try {
      // Create a promise that will be resolved when we get the screenshot back
      const screenshotPromise = new Promise<string>((resolve, reject) => {
        // Store the resolve function so message handler can call it
        screenshot3DResolveRef.current = resolve;

        // Set a timeout in case the 3D viewer doesn't respond
        setTimeout(() => {
          if (screenshot3DResolveRef.current) {
            screenshot3DResolveRef.current = null;
            reject(new Error('Screenshot timeout'));
          }
        }, 5000);
      });

      // Request screenshot from 3D viewer
      console.log('[Copy3D] Requesting screenshot from 3D viewer');
      iframe3DRef.current.contentWindow.postMessage({
        type: 'request_screenshot',
      }, '*');

      // Wait for the screenshot
      const dataUrl = await screenshotPromise;
      console.log('[Copy3D] Got screenshot, copying to clipboard');

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob,
        }),
      ]);

      console.log('[Copy3D] Success!');
      setCopyFeedback('3d');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('[Copy3D] Error:', error);
      alert(t('copyError'));
    } finally {
      setIsCopying3D(false);
      screenshot3DResolveRef.current = null;
    }
  }, [product, t]);

  // Listen for screenshot response from 3D viewer iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, dataUrl, error } = event.data || {};

      if (type === 'screenshot_response' && dataUrl) {
        console.log('[Copy3D] Received screenshot response');
        if (screenshot3DResolveRef.current) {
          screenshot3DResolveRef.current(dataUrl);
          screenshot3DResolveRef.current = null;
        }
      } else if (type === 'screenshot_error') {
        console.error('[Copy3D] Screenshot error from 3D viewer:', error);
        if (screenshot3DResolveRef.current) {
          screenshot3DResolveRef.current = null;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // AI Render - cleanup on unmount
  useEffect(() => {
    return () => {
      if (aiRenderPollRef.current) clearInterval(aiRenderPollRef.current);
      if (aiRenderTimerRef.current) clearInterval(aiRenderTimerRef.current);
      // Clean up video refs
      if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
      if (aiVideoTimerRef.current) clearInterval(aiVideoTimerRef.current);
    };
  }, []);

  // AI Render - Open overlay and capture 3D screenshot
  const handleAiRenderOpen = useCallback(async () => {
    if (!product?.has3DModel || !iframe3DRef.current?.contentWindow) {
      alert(t('no3DModel'));
      return;
    }

    try {
      // Capture 3D screenshot (reuse same postMessage pattern as handleCopy3D)
      const screenshotPromise = new Promise<string>((resolve, reject) => {
        screenshot3DResolveRef.current = resolve;
        setTimeout(() => {
          if (screenshot3DResolveRef.current) {
            screenshot3DResolveRef.current = null;
            reject(new Error('Screenshot timeout'));
          }
        }, 5000);
      });

      iframe3DRef.current.contentWindow.postMessage({ type: 'request_screenshot' }, '*');
      const dataUrl = await screenshotPromise;

      // Fetch product material and original image in parallel
      let materialInfo = '';
      let originalImageBase64: string | null = null;

      const [infoResult, originalResult] = await Promise.allSettled([
        fetch(`/api/kie-ai/product-info/${encodeURIComponent(product.sku)}`).then(r => r.json()),
        fetch(`/api/kie-ai/original-image/${encodeURIComponent(product.sku)}`).then(r => r.json()),
      ]);

      if (infoResult.status === 'fulfilled' && infoResult.value?.success && infoResult.value?.data?.material) {
        materialInfo = `, made of ${infoResult.value.data.material}`;
        console.log('[AIRender] Product info:', infoResult.value.data);
      }

      if (originalResult.status === 'fulfilled' && originalResult.value?.success && originalResult.value?.base64) {
        const orig = originalResult.value;
        originalImageBase64 = `data:${orig.mediaType || 'image/jpeg'};base64,${orig.base64}`;
        console.log('[AIRender] Original product image loaded, size:', orig.base64.length);
      } else {
        console.warn('[AIRender] No original product image available, using 3D screenshot only');
      }

      // Build default prompt - references both images when original is available
      const defaultPrompt = originalImageBase64
        ? `The first image is the original product photo${materialInfo}. The second image shows the same product as a 3D model with a branded logo/design printed on it from a specific viewing angle. Create a high-end mood shot: use the original photo for realistic product appearance and materials, use the 3D model for the exact viewing angle, orientation, and logo/design placement. Place the product on a stylish modern office desk with warm wood tints. Clean, elegant minimalist setting with soft natural lighting, shallow depth of field. Photorealistic commercial photography.`
        : `High-end luxurious mood shot of this product${materialInfo} with branded logo, placed on a stylish modern office desk with warm wood tints. Clean, elegant minimalist setting with soft natural lighting, shallow depth of field, premium lifestyle atmosphere. Photorealistic commercial photography.`;

      // Reset state and open overlay
      setAiRenderScreenshot(dataUrl);
      setAiRenderOriginalImage(originalImageBase64);
      setAiRenderPrompt(defaultPrompt);
      setAiRenderStep('ready');
      setAiRenderResult(null);
      setAiRenderError(null);
      setAiRenderProgress(0);
      setAiRenderElapsed(0);
      setAiRenderTaskId(null);
      // Reset video state
      setAiVideoPhase('ready');
      setAiVideoMoodShot(null);
      setAiVideoResult(null);
      setAiVideoError(null);
      setShowAiRenderOverlay(true);
    } catch (error) {
      console.error('[AIRender] Failed to capture screenshot:', error);
      alert(t('aiRenderError'));
    }
  }, [product, currentTechnique, t]);

  // AI Render - Generate (user clicks "Generate" in overlay)
  const handleAiRenderGenerate = useCallback(async () => {
    if (!aiRenderScreenshot) return;

    setAiRenderStep('generating');
    setAiRenderProgress(0);
    setAiRenderElapsed(0);
    setAiRenderError(null);

    try {
      // Strip data URL prefix for sending
      const base64Data = aiRenderScreenshot.replace(/^data:image\/\w+;base64,/, '');
      const originalBase64Data = aiRenderOriginalImage?.replace(/^data:image\/\w+;base64,/, '') || undefined;

      const payload: Record<string, any> = { base64Data, prompt: aiRenderPrompt, model: 'nano-banana-2', aspect_ratio: '1:1' };
      if (originalBase64Data) {
        payload.originalBase64Data = originalBase64Data;
      }

      const response = await fetch('/api/kie-ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log('[AIRender] Generate API response:', result);

      if (!result.success || !result.data?.taskId) {
        console.error('[AIRender] Generate API error:', result);
        throw new Error(result.error || result.details?.msg || 'Failed to start generation');
      }

      const taskId = result.data.taskId;
      setAiRenderTaskId(taskId);
      console.log('[AIRender] Task created:', taskId);

      // Start 45-second countdown timer
      const startTime = Date.now();
      const maxDuration = 45;
      aiRenderTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setAiRenderElapsed(elapsed);
        setAiRenderProgress(Math.min((elapsed / maxDuration) * 100, 100));
      }, 1000);

      // Start polling every 3 seconds
      aiRenderPollRef.current = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/kie-ai/status/${taskId}`);
          const statusResult = await statusResponse.json();

          if (!statusResult.success) {
            console.error('[AIRender] Poll API error:', statusResult);
            return;
          }

          const { state, resultUrls, failMsg, raw } = statusResult.data;
          console.log('[AIRender] Poll status:', state, 'resultUrls:', resultUrls, 'raw:', raw);

          if (state === 'success' || state === 'completed') {
            // Stop polling and timer
            if (aiRenderPollRef.current) clearInterval(aiRenderPollRef.current);
            if (aiRenderTimerRef.current) clearInterval(aiRenderTimerRef.current);
            aiRenderPollRef.current = null;
            aiRenderTimerRef.current = null;

            const resultUrl = resultUrls?.[0] || null;
            setAiRenderResult(resultUrl);
            setAiRenderStep('success');
          } else if (state === 'fail' || state === 'failed' || state === 'error') {
            if (aiRenderPollRef.current) clearInterval(aiRenderPollRef.current);
            if (aiRenderTimerRef.current) clearInterval(aiRenderTimerRef.current);
            aiRenderPollRef.current = null;
            aiRenderTimerRef.current = null;

            setAiRenderError(failMsg || 'Generation failed');
            setAiRenderStep('error');
          }
        } catch (pollError) {
          console.error('[AIRender] Poll error:', pollError);
        }
      }, 3000);
    } catch (error) {
      console.error('[AIRender] Generate error:', error);
      setAiRenderError(error instanceof Error ? error.message : 'Unknown error');
      setAiRenderStep('error');
      if (aiRenderTimerRef.current) clearInterval(aiRenderTimerRef.current);
      aiRenderTimerRef.current = null;
    }
  }, [aiRenderScreenshot, aiRenderOriginalImage, aiRenderPrompt]);

  // AI Render - Close overlay
  const handleAiRenderClose = useCallback(() => {
    if (aiRenderPollRef.current) clearInterval(aiRenderPollRef.current);
    if (aiRenderTimerRef.current) clearInterval(aiRenderTimerRef.current);
    aiRenderPollRef.current = null;
    aiRenderTimerRef.current = null;
    // Clean up video refs
    if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
    aiVideoPollRef.current = null;
    if (aiVideoTimerRef.current) clearInterval(aiVideoTimerRef.current);
    aiVideoTimerRef.current = null;
    setShowAiRenderOverlay(false);
  }, []);

  // AI Render - Copy result to clipboard
  const handleAiRenderCopy = useCallback(async () => {
    if (!aiRenderResult) return;
    try {
      const response = await fetch(aiRenderResult);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      setCopyFeedback('3d');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('[AIRender] Copy error:', error);
      alert(t('copyError'));
    }
  }, [aiRenderResult, t]);

  // AI Render - Download result
  const handleAiRenderDownload = useCallback(() => {
    if (!aiRenderResult) return;
    const link = document.createElement('a');
    link.href = aiRenderResult;
    link.download = `ai-render-${product?.name || 'product'}-${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [aiRenderResult, product]);

  // AI Render Option 2 - Video generation (two-step: mood shot → video)
  const handleAiRenderGenerateVideo = useCallback(async () => {
    if (!aiRenderScreenshot) return;

    // Reset video state
    setAiVideoPhase('generating-image');
    setAiVideoMoodShot(null);
    setAiVideoResult(null);
    setAiVideoError(null);
    setAiRenderStep('generating');
    setAiRenderElapsed(0);
    setAiRenderProgress(0);

    // Clean up old refs
    if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
    if (aiVideoTimerRef.current) clearInterval(aiVideoTimerRef.current);

    const base64Data = aiRenderScreenshot.replace(/^data:image\/\w+;base64,/, '');
    const originalBase64Data = aiRenderOriginalImage?.replace(/^data:image\/\w+;base64,/, '') || undefined;

    // Start timer
    const startTime = Date.now();
    aiVideoTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setAiRenderElapsed(elapsed);
      setAiRenderProgress(Math.min((elapsed / 120) * 100, 100)); // 2 min total estimate
    }, 1000);

    try {
      // ===== STEP 1: Generate mood shot with nano-banana-2 =====
      console.log('[AIVideo] Step 1: Generating mood shot with nano-banana-2...');
      const payload: Record<string, any> = {
        base64Data,
        prompt: aiRenderPrompt,
        model: 'nano-banana-2',
        aspect_ratio: '1:1',
      };
      if (originalBase64Data) payload.originalBase64Data = originalBase64Data;

      const genResponse = await fetch('/api/kie-ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const genResult = await genResponse.json();

      if (!genResult.success || !genResult.data?.taskId) {
        throw new Error(genResult.error || 'Failed to start mood shot generation');
      }

      const imageTaskId = genResult.data.taskId;
      console.log('[AIVideo] Mood shot task created:', imageTaskId);

      // Poll for mood shot result
      const moodShotUrl = await new Promise<string>((resolve, reject) => {
        aiVideoPollRef.current = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/kie-ai/status/${imageTaskId}`);
            const statusData = await statusRes.json();
            if (!statusData.success) return;

            const { state, resultUrls, failMsg } = statusData.data;
            if (state === 'success' || state === 'completed') {
              if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
              aiVideoPollRef.current = null;
              const url = resultUrls?.[0];
              if (url) {
                resolve(url);
              } else {
                reject(new Error('No result URL from mood shot'));
              }
            } else if (state === 'fail' || state === 'failed' || state === 'error') {
              if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
              aiVideoPollRef.current = null;
              reject(new Error(failMsg || 'Mood shot generation failed'));
            }
          } catch (e) {
            console.error('[AIVideo] Poll error (mood shot):', e);
          }
        }, 3000);
      });

      console.log('[AIVideo] Mood shot complete:', moodShotUrl);
      setAiVideoMoodShot(moodShotUrl);
      setAiVideoPhase('generating-video');

      // ===== STEP 2: Generate video from mood shot =====
      console.log('[AIVideo] Step 2: Generating video from mood shot...');
      const videoResponse = await fetch('/api/kie-ai/generate-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: moodShotUrl,
          prompt: aiVideoPrompt,
          duration: aiVideoDuration,
          resolution: aiVideoResolution,
        }),
      });
      const videoResult = await videoResponse.json();

      if (!videoResult.success || !videoResult.data?.taskId) {
        throw new Error(videoResult.error || 'Failed to start video generation');
      }

      const videoTaskId = videoResult.data.taskId;
      console.log('[AIVideo] Video task created:', videoTaskId);

      // Poll for video result
      const videoUrl = await new Promise<string>((resolve, reject) => {
        aiVideoPollRef.current = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/kie-ai/status/${videoTaskId}`);
            const statusData = await statusRes.json();
            if (!statusData.success) return;

            const { state, resultUrls, failMsg } = statusData.data;
            if (state === 'success' || state === 'completed') {
              if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
              aiVideoPollRef.current = null;
              const url = resultUrls?.[0];
              if (url) {
                resolve(url);
              } else {
                reject(new Error('No result URL from video'));
              }
            } else if (state === 'fail' || state === 'failed' || state === 'error') {
              if (aiVideoPollRef.current) clearInterval(aiVideoPollRef.current);
              aiVideoPollRef.current = null;
              reject(new Error(failMsg || 'Video generation failed'));
            }
          } catch (e) {
            console.error('[AIVideo] Poll error (video):', e);
          }
        }, 3000);
      });

      console.log('[AIVideo] Video complete:', videoUrl);
      setAiVideoResult(videoUrl);
      setAiVideoPhase('success');
      setAiRenderStep('success');
    } catch (error) {
      console.error('[AIVideo] Error:', error);
      setAiVideoError(error instanceof Error ? error.message : 'Unknown error');
      setAiVideoPhase('error');
      setAiRenderStep('error');
    } finally {
      if (aiVideoTimerRef.current) {
        clearInterval(aiVideoTimerRef.current);
        aiVideoTimerRef.current = null;
      }
    }
  }, [aiRenderScreenshot, aiRenderOriginalImage, aiRenderPrompt, aiVideoPrompt, aiVideoDuration, aiVideoResolution]);

  // RemoveBG handler - uses backend proxy to remove.bg API
  const handleRemoveBg = useCallback(async () => {
    if (!fabricCanvasRef.current) return;

    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (!activeObject || activeObject.type !== 'image') {
      // If no image selected, try to get first image on canvas
      const objects = fabricCanvasRef.current.getObjects('image');
      if (objects.length === 0) {
        alert(t('noImageForRemoveBg'));
        return;
      }
    }

    setIsRemovingBg(true);

    try {
      // Export current canvas/selected image as base64
      const imageToProcess = activeObject || fabricCanvasRef.current.getObjects('image')[0];
      if (!imageToProcess) {
        throw new Error('Geen afbeelding gevonden');
      }

      // Get the image data URL
      const tempCanvas = document.createElement('canvas');
      const img = imageToProcess as fabric.Image;
      tempCanvas.width = img.width || 500;
      tempCanvas.height = img.height || 500;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx && img._element) {
        tempCtx.drawImage(img._element, 0, 0);
      }
      const imageDataUrl = tempCanvas.toDataURL('image/png');

      // Call rembg Docker service for background removal
      const response = await fetch('/rembg/remove-background-base64', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: imageDataUrl,
          model: 'u2net',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.image) {
        throw new Error(result.detail || 'Rembg API error');
      }

      // Load the result back onto canvas (result.image contains data:image/png;base64,...)
      const resultUrl = result.image;

      fabric.Image.fromURL(resultUrl, (newImg) => {
        if (!fabricCanvasRef.current || !newImg) return;

        // Copy position/scale from original
        newImg.set({
          left: imageToProcess.left,
          top: imageToProcess.top,
          scaleX: imageToProcess.scaleX,
          scaleY: imageToProcess.scaleY,
          angle: imageToProcess.angle,
          originX: imageToProcess.originX,
          originY: imageToProcess.originY,
        });

        // Replace the original image
        fabricCanvasRef.current.remove(imageToProcess);
        fabricCanvasRef.current.add(newImg);
        fabricCanvasRef.current.setActiveObject(newImg);
        fabricCanvasRef.current.renderAll();

        // Update preview
        setTimeout(() => updateLogoPreview(), 100);
      });
    } catch (error) {
      console.error('RemoveBG error:', error);
      alert(t('removeBgError'));
    } finally {
      setIsRemovingBg(false);
    }
  }, [updateLogoPreview, t]);

  // Vectorize handler - converts raster image to SVG
  const handleVectorize = useCallback(async () => {
    if (!fabricCanvasRef.current) return;

    const objects = fabricCanvasRef.current.getObjects('image');
    if (objects.length === 0) {
      alert(t('noImageForVectorize'));
      return;
    }

    // Harry Potter spells for the magical vectorization experience
    const spells = [
      'Vectorium Transformare!',
      'Pixelus Eliminatus!',
      'Curvus Perfectus!',
      'Pathfinder Maxima!',
      'SVG Revelio!',
      'Smoothicus Bezierus!',
      'Expecto Vectorum!',
    ];

    setIsVectorizing(true);
    setShowVectorizeModal(true);
    setVectorizeProgress(0);
    setCurrentSpell(spells[0]);

    // Animate progress with spells over 10 seconds (1000ms * 10 steps = 10000ms)
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 10; // 10% per step, 10 steps = 10 seconds
      if (progress > 95) progress = 95;
      setVectorizeProgress(progress);
      // Change spell every ~15%
      const spellIndex = Math.min(Math.floor(progress / 15), spells.length - 1);
      setCurrentSpell(spells[spellIndex]);
    }, 1000);

    try {
      const img = objects[0] as fabric.Image;
      if (!img._element) {
        throw new Error('Afbeelding element niet gevonden');
      }

      // Create a temporary canvas with the image
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width || 500;
      tempCanvas.height = img.height || 500;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) throw new Error('Canvas context niet beschikbaar');

      tempCtx.drawImage(img._element, 0, 0);
      const base64Data = tempCanvas.toDataURL('image/png');

      // Check if current technique is Laser - if so, use monochrome (1 color)
      const isLaserTechnique = currentTechnique?.code?.startsWith('L');

      // Call potrace API endpoint for better vectorization
      // Laser techniques: monochrome (1 color - stainless steel gray)
      // Other techniques: preserve original colors
      const response = await fetch('/api/logos/vectorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64Data,
          threshold: 50,
          turdsize: 2,
          alphamax: 1,
          colorMode: !isLaserTechnique,  // false for Laser = monochrome, true for others = colors
          numberOfColors: isLaserTechnique ? 1 : 16,
          color: isLaserTechnique ? '#7A8288' : '#000000', // Stainless steel gray for laser
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Vectorization failed');
      }

      // Get SVG content - use svgContent if available, otherwise decode from base64 data URL
      let svgString = result.data.svgContent;
      if (!svgString && result.data.svg) {
        // Decode base64 data URL
        const base64Match = result.data.svg.match(/^data:image\/svg\+xml;base64,(.+)$/);
        if (base64Match) {
          svgString = atob(base64Match[1]);
        } else {
          svgString = result.data.svg;
        }
      }

      console.log('SVG to load (first 300 chars):', svgString?.substring(0, 300));

      if (!svgString || !svgString.includes('<svg')) {
        throw new Error('Invalid SVG response from server');
      }

      // Load SVG onto canvas
      fabric.loadSVGFromString(svgString, (svgObjects, options) => {
        console.log('SVG loaded, objects:', svgObjects?.length, 'options:', options);
        if (!fabricCanvasRef.current) return;

        if (!svgObjects || svgObjects.length === 0) {
          console.error('No SVG objects parsed from string');
          alert('Kon SVG niet laden - geen objecten gevonden');
          return;
        }

        const svgGroup = fabric.util.groupSVGElements(svgObjects, options);

        // Scale to fit original image size
        const scaleX = (img.width || 500) / (svgGroup.width || 500);
        const scaleY = (img.height || 500) / (svgGroup.height || 500);

        svgGroup.set({
          left: img.left,
          top: img.top,
          scaleX: (img.scaleX || 1) * scaleX,
          scaleY: (img.scaleY || 1) * scaleY,
          originX: img.originX,
          originY: img.originY,
        });

        // Replace original image with SVG
        fabricCanvasRef.current.remove(img);
        fabricCanvasRef.current.add(svgGroup);
        fabricCanvasRef.current.setActiveObject(svgGroup);
        fabricCanvasRef.current.renderAll();

        // Update preview
        setTimeout(() => updateLogoPreview(), 100);

        // Complete the progress animation
        clearInterval(progressInterval);
        setVectorizeProgress(100);
        setCurrentSpell('Abracadabra!');

        // Close modal after a short delay
        setTimeout(() => {
          setShowVectorizeModal(false);
          setVectorizeProgress(0);
        }, 800);
      });
    } catch (error) {
      console.error('Vectorize error:', error);
      clearInterval(progressInterval);
      setShowVectorizeModal(false);
      alert(t('vectorizeError'));
    } finally {
      setIsVectorizing(false);
    }
  }, [currentTechnique, updateLogoPreview, t]);

  // Bucket fill - fill all SVG paths with selected color
  const handleFillAll = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'path' || obj.type === 'polygon') {
        (obj as fabric.Path).set('fill', bucketColor);
      } else if (obj.type === 'group') {
        (obj as fabric.Group).getObjects().forEach((subObj) => {
          if (subObj.type === 'path' || subObj.type === 'polygon') {
            (subObj as fabric.Path).set('fill', bucketColor);
          }
        });
      }
    });
    canvas.renderAll();
    setTimeout(() => updateLogoPreview(), 100);
  }, [bucketColor, updateLogoPreview]);

  // Download logo as EPS - exports only the vectorized logo, not canvas
  const handleDownloadEPS = useCallback(async () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    const contentObjects = objects.filter((obj: fabric.Object) => !(obj as any).isPrintArea);
    if (contentObjects.length === 0) { alert('Geen logo gevonden'); return; }

    // Get the logo object (first content object, usually an SVG group)
    const logoObj = contentObjects[0];

    // Export just the logo object as SVG
    let svgData: string;

    if (logoObj.type === 'group') {
      // For SVG groups, export the group directly
      const group = logoObj as fabric.Group;
      const groupBounds = group.getBoundingRect();
      const width = groupBounds.width;
      const height = groupBounds.height;

      // Clone the group and reset its position for clean export
      const clonedGroup = fabric.util.object.clone(group);
      clonedGroup.set({
        left: width / 2,
        top: height / 2,
        originX: 'center',
        originY: 'center',
      });

      // Create a temporary canvas for clean SVG export
      const tempCanvas = new fabric.StaticCanvas(null, { width, height });
      tempCanvas.add(clonedGroup);
      svgData = tempCanvas.toSVG();
      tempCanvas.dispose();
    } else {
      // For single objects, export with tight bounds
      const bounds = logoObj.getBoundingRect();
      const width = bounds.width + 10;
      const height = bounds.height + 10;

      // Create temp canvas with just the logo
      const tempCanvas = new fabric.StaticCanvas(null, { width, height });
      const clonedObj = fabric.util.object.clone(logoObj);
      clonedObj.set({
        left: width / 2,
        top: height / 2,
        originX: 'center',
        originY: 'center',
      });
      tempCanvas.add(clonedObj);
      svgData = tempCanvas.toSVG();
      tempCanvas.dispose();
    }

    try {
      const response = await fetch('/api/logos/convert-to-eps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ svg: svgData }),
      });
      const result = await response.json();
      if (result.success && result.data?.eps_url) {
        const link = document.createElement('a');
        link.href = result.data.eps_url;
        link.download = result.data.filename || 'logo.eps';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else { throw new Error(result.error || 'EPS failed'); }
    } catch (error) { console.error('EPS error:', error); alert('EPS download mislukt'); }
  }, []);


  // Start crop mode - create a crop selection rectangle over the selected image or group
  const handleStartCrop = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();

    // Find any content object to crop (image, group, or path - excluding print area)
    let imageToCrop = activeObject;
    if (!imageToCrop || (imageToCrop as any).isPrintArea || (imageToCrop as any).isCropRect) {
      // Find the first content object on the canvas
      const contentObjects = canvas.getObjects().filter(
        (obj: fabric.Object) => !(obj as any).isPrintArea && !(obj as any).isCropRect
      );
      if (contentObjects.length === 0) {
        alert(t('noImageForCrop'));
        return;
      }
      imageToCrop = contentObjects[0];
    }

    // Get the image bounds
    const imgBounds = imageToCrop.getBoundingRect();

    // Create crop selection rectangle
    const cropRect = new fabric.Rect({
      left: imgBounds.left + imgBounds.width * 0.1,
      top: imgBounds.top + imgBounds.height * 0.1,
      width: imgBounds.width * 0.8,
      height: imgBounds.height * 0.8,
      fill: 'rgba(0, 0, 0, 0.3)',
      stroke: '#002B51',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      cornerColor: '#002B51',
      cornerStyle: 'circle',
      cornerSize: 12,
      transparentCorners: false,
      hasRotatingPoint: false,
      lockRotation: true,
    });

    // Mark as crop rectangle
    (cropRect as any).isCropRect = true;
    (cropRect as any).targetImage = imageToCrop;

    // Store reference
    cropRectRef.current = cropRect;

    // Add to canvas and select it
    canvas.add(cropRect);
    canvas.setActiveObject(cropRect);
    canvas.renderAll();

    setIsCropping(true);
  }, [t]);

  // Apply the crop
  const handleApplyCrop = useCallback(async () => {
    if (!fabricCanvasRef.current || !cropRectRef.current) return;

    const canvas = fabricCanvasRef.current;
    const cropRect = cropRectRef.current;
    const targetObject = (cropRect as any).targetImage as fabric.Object;

    if (!targetObject) {
      handleCancelCrop();
      return;
    }

    // Get crop rectangle bounds (in canvas coordinates)
    const cropBounds = cropRect.getBoundingRect();
    const objBounds = targetObject.getBoundingRect();
    const scaleX = targetObject.scaleX || 1;
    const scaleY = targetObject.scaleY || 1;

    // Calculate the intersection of crop rect and object bounds
    const intersectLeft = Math.max(cropBounds.left, objBounds.left);
    const intersectTop = Math.max(cropBounds.top, objBounds.top);
    const intersectRight = Math.min(cropBounds.left + cropBounds.width, objBounds.left + objBounds.width);
    const intersectBottom = Math.min(cropBounds.top + cropBounds.height, objBounds.top + objBounds.height);

    // Check if there's a valid intersection
    if (intersectRight <= intersectLeft || intersectBottom <= intersectTop) {
      alert(t('cropOutsideImage'));
      handleCancelCrop();
      return;
    }

    // Create a source canvas from the target object
    let sourceCanvas: HTMLCanvasElement;
    let sourceWidth: number;
    let sourceHeight: number;

    if (targetObject.type === 'image') {
      // For images, use the native element
      const imgElement = (targetObject as fabric.Image)._element as HTMLImageElement;
      if (!imgElement) {
        handleCancelCrop();
        return;
      }
      sourceWidth = imgElement.naturalWidth || imgElement.width;
      sourceHeight = imgElement.naturalHeight || imgElement.height;
      sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = sourceWidth;
      sourceCanvas.height = sourceHeight;
      const ctx = sourceCanvas.getContext('2d');
      if (!ctx) {
        handleCancelCrop();
        return;
      }
      ctx.drawImage(imgElement, 0, 0);
    } else {
      // For groups (SVG logos), render to canvas
      const group = targetObject as fabric.Group;
      const groupWidth = group.width || 100;
      const groupHeight = group.height || 100;
      sourceWidth = groupWidth;
      sourceHeight = groupHeight;
      sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = groupWidth;
      sourceCanvas.height = groupHeight;

      // Create a temp fabric canvas to render the group
      const tempFabricCanvas = new fabric.StaticCanvas(null, { width: groupWidth, height: groupHeight });

      // Clone the group and center it
      const clonedGroup = fabric.util.object.clone(group);
      clonedGroup.set({
        left: groupWidth / 2,
        top: groupHeight / 2,
        originX: 'center',
        originY: 'center',
        scaleX: 1,
        scaleY: 1,
        angle: 0,
      });
      tempFabricCanvas.add(clonedGroup);
      tempFabricCanvas.renderAll();

      // Get the canvas element
      const tempEl = tempFabricCanvas.getElement();
      const ctx = sourceCanvas.getContext('2d');
      if (!ctx) {
        tempFabricCanvas.dispose();
        handleCancelCrop();
        return;
      }
      ctx.drawImage(tempEl, 0, 0);
      tempFabricCanvas.dispose();
    }

    // Calculate source coordinates in original pixels
    const srcX = (intersectLeft - objBounds.left) / scaleX;
    const srcY = (intersectTop - objBounds.top) / scaleY;
    const srcWidth = (intersectRight - intersectLeft) / scaleX;
    const srcHeight = (intersectBottom - intersectTop) / scaleY;

    // Create a temporary canvas with the cropped size
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(srcWidth);
    tempCanvas.height = Math.round(srcHeight);
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      handleCancelCrop();
      return;
    }

    // Draw the cropped portion from source
    tempCtx.drawImage(
      sourceCanvas,
      Math.round(srcX), Math.round(srcY), Math.round(srcWidth), Math.round(srcHeight),
      0, 0, Math.round(srcWidth), Math.round(srcHeight)
    );

    // Auto-trim: remove transparent pixels from all edges
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const { data, width, height } = imageData;

    // Find bounds of non-transparent pixels
    let minX = width, minY = height, maxX = 0, maxY = 0;
    let hasContent = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 10) {
          hasContent = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!hasContent) {
      minX = 0;
      minY = 0;
      maxX = width - 1;
      maxY = height - 1;
    }

    // Create trimmed canvas
    const trimWidth = maxX - minX + 1;
    const trimHeight = maxY - minY + 1;
    const trimCanvas = document.createElement('canvas');
    trimCanvas.width = trimWidth;
    trimCanvas.height = trimHeight;
    const trimCtx = trimCanvas.getContext('2d');

    if (!trimCtx) {
      handleCancelCrop();
      return;
    }

    trimCtx.drawImage(
      tempCanvas,
      minX, minY, trimWidth, trimHeight,
      0, 0, trimWidth, trimHeight
    );

    // Get the trimmed image data URL
    const croppedDataUrl = trimCanvas.toDataURL('image/png');

    // Calculate position for cropped image
    const trimOffsetX = minX * scaleX;
    const trimOffsetY = minY * scaleY;
    const trimmedIntersectLeft = intersectLeft + trimOffsetX;
    const trimmedIntersectTop = intersectTop + trimOffsetY;
    const trimmedWidth = trimWidth * scaleX;
    const trimmedHeight = trimHeight * scaleY;

    // Remove crop rectangle
    canvas.remove(cropRect);
    cropRectRef.current = null;

    // Load the cropped image
    fabric.Image.fromURL(croppedDataUrl, (croppedImg) => {
      if (!croppedImg || !fabricCanvasRef.current) return;

      const newScaleX = trimmedWidth / trimWidth;
      const newScaleY = trimmedHeight / trimHeight;

      croppedImg.set({
        left: trimmedIntersectLeft,
        top: trimmedIntersectTop,
        scaleX: newScaleX,
        scaleY: newScaleY,
        originX: 'left',
        originY: 'top',
      });

      canvas.remove(targetObject);
      canvas.add(croppedImg);
      croppedImg.setCoords();
      canvas.setActiveObject(croppedImg);

      // Redraw print area if it was lost
      if ((canvas as any).drawPrintArea) {
        const currentTech = techniques.find(t => t.code === selectedTechnique) || techniques[0];
        if (currentTech) {
          (canvas as any).drawPrintArea(currentTech);
        }
      }

      canvas.requestRenderAll();

      setTimeout(() => updateLogoPreview(), 100);
    });

    setIsCropping(false);
  }, [updateLogoPreview, t]);

  // Cancel crop mode
  const handleCancelCrop = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    // Remove crop rectangle if exists
    if (cropRectRef.current) {
      canvas.remove(cropRectRef.current);
      cropRectRef.current = null;
    }

    canvas.renderAll();
    setIsCropping(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="magic-logo-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* Magical Vectorize Progress Modal */}
      {showVectorizeModal && (
        <div className="vectorize-magic-modal">
          <div className="vectorize-modal-box">
            <div className="vectorize-modal-header">
              <h2><i className="fas fa-magic"></i> Vectoriseren</h2>
            </div>
            <div className="vectorize-modal-body">
              <div className="magic-wand">🪄</div>
              <h3 className="spell-text">{currentSpell}</h3>
              <div className="magic-progress-container">
                <div
                  className="magic-progress-bar"
                  style={{ width: `${vectorizeProgress}%` }}
                />
              </div>
              <p className="progress-percent">{Math.round(vectorizeProgress)}%</p>
            </div>
          </div>
        </div>
      )}

      <div className="magic-logo-modal">
        {/* Header */}
        <div className="modal-header">
          <h2><i className="fas fa-pencil-alt"></i> Magic Logotool</h2>
          <div className="language-switcher">
            <button
              className={`flag-btn ${language === 'de' ? 'active' : ''}`}
              onClick={() => setLanguage('de')}
              title="Deutsch"
            >
              <img src={`${import.meta.env.BASE_URL}flags/de.png`} alt="DE" />
            </button>
            <button
              className={`flag-btn ${language === 'nl' ? 'active' : ''}`}
              onClick={() => setLanguage('nl')}
              title="Nederlands"
            >
              <img src={`${import.meta.env.BASE_URL}flags/nl.png`} alt="NL" />
            </button>
            <button
              className={`flag-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
              title="English"
            >
              <img src={`${import.meta.env.BASE_URL}flags/en.png`} alt="EN" />
            </button>
          </div>
          <div className="editor-mode-toggle">
            <button
              className={`mode-btn ${editorMode === 'basic' ? 'active' : ''}`}
              onClick={() => setEditorMode('basic')}
            >
              {t('basicMode')}
            </button>
            <button
              className={`mode-btn ${editorMode === 'advanced' ? 'active' : ''}`}
              onClick={() => setEditorMode('advanced')}
            >
              {t('advancedMode')}
            </button>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Initial Loader - 3 gears with "This is where magic happens" */}
        {showInitialLoader && (
          <div className="initial-loader-overlay">
            <div className="initial-loader">
              <div className="gears-container">
                <i className="fas fa-cog gear gear-1"></i>
                <i className="fas fa-cog gear gear-2"></i>
                <i className="fas fa-cog gear gear-3"></i>
              </div>
              <div className="loader-text">This is where magic happens</div>
            </div>
          </div>
        )}

        {/* Body - 60/40 split */}
        <div className={`modal-body ${editorMode === 'basic' ? 'basic-mode' : 'advanced-mode'}`}>
          {/* Left Toolbar - Only in Advanced mode */}
          {editorMode === 'advanced' && (
          <div className="editor-toolbar">
            <button className="toolbar-btn" title="Select" onClick={() => {
              if (fabricCanvasRef.current) {
                fabricCanvasRef.current.isDrawingMode = false;
              }
            }}>
              <i className="fas fa-mouse-pointer"></i>
            </button>
            <button
              className={`toolbar-btn ${isCropping ? 'active' : ''}`}
              title={isCropping ? 'Cropping...' : 'Crop'}
              onClick={isCropping ? undefined : handleStartCrop}
              disabled={!hasLogo}
            >
              <i className="fas fa-crop-alt"></i>
            </button>
            <div className="toolbar-divider" />
            <button className="toolbar-btn" title="Flip Horizontal" onClick={handleFlipH}>
              <i className="fas fa-arrows-alt-h"></i>
            </button>
            <button className="toolbar-btn" title="Flip Vertical" onClick={handleFlipV}>
              <i className="fas fa-arrows-alt-v"></i>
            </button>
            <button className="toolbar-btn" title="Rotate" onClick={handleRotate}>
              <i className="fas fa-redo"></i>
            </button>
            <div className="toolbar-divider" />
            <button className="toolbar-btn" title="Draw" onClick={() => {
              if (fabricCanvasRef.current) {
                fabricCanvasRef.current.isDrawingMode = !fabricCanvasRef.current.isDrawingMode;
                fabricCanvasRef.current.freeDrawingBrush.color = '#002B51';
                fabricCanvasRef.current.freeDrawingBrush.width = 3;
              }
            }}>
              <i className="fas fa-pencil-alt"></i>
            </button>
            <button className="toolbar-btn" title="Add Text" onClick={() => {
              if (fabricCanvasRef.current) {
                const cw = fabricCanvasRef.current.getWidth();
                const ch = fabricCanvasRef.current.getHeight();
                const text = new fabric.IText('Text', {
                  left: cw / 2,
                  top: ch / 2,
                  originX: 'center',
                  originY: 'center',
                  fontSize: 32,
                  fill: selectedTextColor,
                  fontFamily: selectedFontFamily,
                });
                fabricCanvasRef.current.add(text);
                fabricCanvasRef.current.setActiveObject(text);
                fabricCanvasRef.current.renderAll();
                setHasLogo(true);
                setIsTextSelected(true);
              }
            }}>
              <i className="fas fa-font"></i>
            </button>
            {/* Text formatting controls - visible when text is selected */}
            {isTextSelected && (
              <>
                <div className="toolbar-divider" />
                <select
                  className="toolbar-select"
                  value={selectedFontFamily}
                  onChange={(e) => {
                    const newFont = e.target.value;
                    setSelectedFontFamily(newFont);
                    if (fabricCanvasRef.current) {
                      const activeObj = fabricCanvasRef.current.getActiveObject();
                      if (activeObj && activeObj.type === 'i-text') {
                        (activeObj as fabric.IText).set('fontFamily', newFont);
                        fabricCanvasRef.current.renderAll();
                        setTimeout(() => updateLogoPreviewRef.current?.(), 100);
                      }
                    }
                  }}
                  title="Font Family"
                >
                  {availableFonts.map((font) => (
                    <option key={font} value={font} style={{ fontFamily: font }}>
                      {font}
                    </option>
                  ))}
                </select>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    className="toolbar-color-picker"
                    value={selectedTextColor}
                    onChange={(e) => {
                      const newColor = e.target.value;
                      setSelectedTextColor(newColor);
                      if (fabricCanvasRef.current) {
                        const activeObj = fabricCanvasRef.current.getActiveObject();
                        if (activeObj && activeObj.type === 'i-text') {
                          (activeObj as fabric.IText).set('fill', newColor);
                          fabricCanvasRef.current.renderAll();
                          setTimeout(() => updateLogoPreviewRef.current?.(), 100);
                        }
                      }
                    }}
                    title="Text Color"
                  />
                  <div className="color-swatches">
                    {textColors.map((color) => (
                      <button
                        key={color}
                        className={`color-swatch ${selectedTextColor === color ? 'active' : ''}`}
                        style={{ backgroundColor: color, border: color === '#FFFFFF' ? '1px solid #ccc' : 'none' }}
                        onClick={() => {
                          setSelectedTextColor(color);
                          if (fabricCanvasRef.current) {
                            const activeObj = fabricCanvasRef.current.getActiveObject();
                            if (activeObj && activeObj.type === 'i-text') {
                              (activeObj as fabric.IText).set('fill', color);
                              fabricCanvasRef.current.renderAll();
                              setTimeout(() => updateLogoPreviewRef.current?.(), 100);
                            }
                          }
                        }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="toolbar-divider" />
            <button className="toolbar-btn" title="Add Circle" onClick={() => {
              if (fabricCanvasRef.current) {
                const cw = fabricCanvasRef.current.getWidth();
                const ch = fabricCanvasRef.current.getHeight();
                const circle = new fabric.Circle({
                  left: cw / 2 - 50,
                  top: ch / 2 - 50,
                  radius: 50,
                  fill: 'transparent',
                  stroke: '#002B51',
                  strokeWidth: 2,
                });
                fabricCanvasRef.current.add(circle);
                fabricCanvasRef.current.setActiveObject(circle);
                fabricCanvasRef.current.renderAll();
              }
            }}>
              <i className="fas fa-circle"></i>
            </button>
            <button className="toolbar-btn" title="Add Line" onClick={() => {
              if (fabricCanvasRef.current) {
                const cw = fabricCanvasRef.current.getWidth();
                const ch = fabricCanvasRef.current.getHeight();
                const line = new fabric.Line([cw * 0.2, ch / 2, cw * 0.8, ch / 2], {
                  stroke: '#002B51',
                  strokeWidth: 2,
                });
                fabricCanvasRef.current.add(line);
                fabricCanvasRef.current.setActiveObject(line);
                fabricCanvasRef.current.renderAll();
              }
            }}>
              <i className="fas fa-minus"></i>
            </button>
            <div className="toolbar-divider" />
            <button className="toolbar-btn danger" title="Delete" onClick={handleDelete}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
          )}

          {/* Center: Editor (60%) */}
          <div className="designer-container">
            {/* Hidden file input - shared between modes */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".jpg,.jpeg,.png,.svg,.ai,.eps,.pdf,image/jpeg,image/png,image/svg+xml,application/pdf,application/postscript"
              style={{ display: 'none' }}
            />

            {/* Top Action Bar - Only in Advanced mode */}
            {editorMode === 'advanced' && (
            <div className="actions-bar">
              <button className="action-btn" onClick={() => fileInputRef.current?.click()}>
                <i className="fas fa-upload"></i> {t('upload')}
              </button>
              <div style={{ position: 'relative' }}>
                <button className={`action-btn ${showRecentPanel ? 'active' : ''}`} onClick={handleRecentClick}>
                  <i className={`fas ${isLoadingRecent ? 'fa-spinner fa-spin' : 'fa-history'}`}></i> {t('recent')}
                </button>
                {/* Recent Logos Panel */}
                {showRecentPanel && (
                  <div className="recent-logos-panel" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '12px',
                    minWidth: '300px',
                    maxWidth: '400px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {isLoadingRecent ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                      </div>
                    ) : recentLogos.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        No recent logos found
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                        {recentLogos.map((logo) => (
                          <div
                            key={logo.id}
                            onClick={() => handleSelectRecentLogo(logo)}
                            style={{
                              cursor: 'pointer',
                              border: '1px solid #eee',
                              borderRadius: '4px',
                              padding: '4px',
                              backgroundColor: '#f9f9f9',
                              transition: 'all 0.2s',
                              aspectRatio: '1'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#007bff';
                              e.currentTarget.style.backgroundColor = '#f0f7ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#eee';
                              e.currentTarget.style.backgroundColor = '#f9f9f9';
                            }}
                            title={logo.originalFilename}
                          >
                            <RecentLogoThumbnail logo={logo} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button className="action-btn" onClick={handleRemoveBg} disabled={!hasLogo || isRemovingBg}>
                <i className={`fas ${isRemovingBg ? 'fa-spinner fa-spin' : 'fa-eraser'}`}></i> {isRemovingBg ? t('removingBg') : t('removeBg')}
              </button>
              <button className="action-btn" onClick={handleVectorize} disabled={!hasLogo || isVectorizing}>
                <i className={`fas ${isVectorizing ? 'fa-spinner fa-spin' : 'fa-bezier-curve'}`}></i> {isVectorizing ? t('vectorizing') : t('vectorize')}
              </button>
              {/* Bucket Fill Tool */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "10px", padding: "5px 10px", background: isBucketMode ? "#e91e63" : "#f0f0f0", borderRadius: "6px" }}>
                <button className="action-btn" onClick={() => setIsBucketMode(!isBucketMode)} style={{ background: isBucketMode ? "white" : undefined, color: isBucketMode ? "#e91e63" : undefined }} title="Bucket Fill">
                  <i className="fas fa-fill-drip"></i>
                </button>
                <input type="color" value={bucketColor} onChange={(e) => setBucketColor(e.target.value)} style={{ width: "30px", height: "30px", border: "none", borderRadius: "4px", cursor: "pointer" }} />
                {/* Palette colors from wizard */}
                {paletteColors.length > 0 && (
                  <div style={{ display: "flex", gap: "4px", marginLeft: "4px" }}>
                    {paletteColors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setBucketColor(color);
                          // Immediately paint all paths with this color
                          if (fabricCanvasRef.current) {
                            const canvas = fabricCanvasRef.current;
                            canvas.getObjects().forEach((obj) => {
                              if (obj.type === 'path' || obj.type === 'polygon') {
                                (obj as fabric.Path).set('fill', color);
                              } else if (obj.type === 'group') {
                                (obj as fabric.Group).getObjects().forEach((subObj) => {
                                  if (subObj.type === 'path' || subObj.type === 'polygon') {
                                    (subObj as fabric.Path).set('fill', color);
                                  }
                                });
                              }
                            });
                            canvas.renderAll();
                            setTimeout(() => updateLogoPreview(), 100);
                          }
                        }}
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: color,
                          border: bucketColor === color ? "2px solid #333" : "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        title={color}
                      />
                    ))}
                  </div>
                )}
                <button className="action-btn" onClick={handleFillAll} disabled={!hasLogo} title="Fill All"><i className="fas fa-paint-roller"></i></button>
                <button className="action-btn" onClick={handleDownloadEPS} disabled={!hasLogo} title="Download EPS"><i className="fas fa-download"></i> EPS</button>
              </div>

              <button className="action-btn danger" onClick={handleClear} disabled={isCropping}>
                <i className="fas fa-times-circle"></i> {t('clear')}
              </button>

              {/* Crop mode buttons */}
              {isCropping && (
                <>
                  <button className="action-btn success" onClick={handleApplyCrop}>
                    <i className="fas fa-check"></i> {t('applyCrop')}
                  </button>
                  <button className="action-btn" onClick={handleCancelCrop}>
                    <i className="fas fa-times"></i> {t('cancelCrop')}
                  </button>
                </>
              )}

              <div className="actions-spacer" />

              <button className="action-btn" onClick={handleReset}>
                <i className="fas fa-undo"></i> {t('undo')}
              </button>
              <button className="action-btn success" onClick={handleSave}>
                <i className="fas fa-check"></i> {t('save')}
              </button>
            </div>
            )}

            {/* Basic mode action bar - simplified: only upload and clear */}
            {editorMode === 'basic' && (
            <div className="actions-bar basic-mode-bar">
              <button className="action-btn primary" onClick={() => fileInputRef.current?.click()}>
                <i className="fas fa-upload"></i> {t('upload')}
              </button>
              <button className="action-btn danger" onClick={handleClear} disabled={!hasLogo}>
                <i className="fas fa-times-circle"></i> {t('clear')}
              </button>
            </div>
            )}

            {/* Basic Mode - Product Preview with Logo */}
            {editorMode === 'basic' && (
              <div className="basic-product-preview" ref={previewContainerRef}>
                {product?.imageUrl ? (
                  <div className="basic-preview-container" style={{ transform: `scale(${previewZoom})`, transformOrigin: 'center center', transition: 'transform 0.1s ease-out' }}>
                    <div className="basic-preview-image-wrapper" ref={basicPreviewWrapperRef}>
                      <img src={product.imageUrl} alt={product.name} className={`basic-product-image ${product.sku?.startsWith('376') ? 'product-376' : ''}`} draggable={false} />
                      {/* Logo overlay - positioned based on technique centroid */}
                      {logoPreviewUrl && currentTechnique && product.svgWidth && product.svgHeight && (
                        (() => {
                          // Get image bounds within SVG viewBox
                          const imgX = product.svgImageX || 0;
                          const imgY = product.svgImageY || 0;
                          const imgW = product.svgImageWidth || product.svgWidth;
                          const imgH = product.svgImageHeight || product.svgHeight;

                          // Apply scale compensation for API coordinate mismatch
                          const printCenterX = currentTechnique.centroidX || product.svgWidth / 2;
                          const printCenterY = currentTechnique.centroidY || product.svgHeight / 2;
                          const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
                          const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
                          const printLeftPx = printCenterX - printAreaWidthPx / 2;
                          const printTopPx = printCenterY - printAreaHeightPx / 2;
                          const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
                          const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);
                          // Convert to image-relative percentage
                          const leftPercent = (logoCenterXPx / product.svgWidth) * 100;
                          const topPercent = (logoCenterYPx / product.svgHeight) * 100;
                          const logoWidthPx = logoRelativePos.scaleX * printAreaWidthPx;
                          const logoHeightPx = logoRelativePos.scaleY * printAreaHeightPx;
                          const widthPercent = (logoWidthPx / product.svgWidth) * 100;
                          const heightPercent = (logoHeightPx / product.svgHeight) * 100;

                          // Check technique type for different effects
                          const isEmboss = currentTechnique?.code?.startsWith('P');
                          const isLaser = currentTechnique?.code?.startsWith('L');
                          const isEmbroidery = currentTechnique?.code === 'E' || currentTechnique?.code?.toLowerCase().includes('embroid') || currentTechnique?.name?.toLowerCase().includes('bordur') || currentTechnique?.name?.toLowerCase().includes('embroid');

                          // Determine filter based on technique
                          let filterStyle = 'none';
                          let opacityStyle = 1;

                          if (isEmboss) {
                            // Emboss/Preging: dark gray/black with 3D relief effect
                            filterStyle = 'grayscale(100%) brightness(0.3) contrast(1.3) drop-shadow(2px 2px 1px rgba(0,0,0,0.7)) drop-shadow(-1px -1px 1px rgba(255,255,255,0.3))';
                            opacityStyle = 0.95;
                          } else if (isLaser) {
                            // Laser: use vectorized gray version, no extra filter needed
                            filterStyle = 'none';
                            opacityStyle = 1;
                          } else if (isEmbroidery) {
                            // Embroidery/Borduren: stronger stitch effect with saturated colors and texture
                            filterStyle = 'saturate(1.3) contrast(1.2) brightness(1.05) drop-shadow(1px 1px 0px rgba(0,0,0,0.5)) drop-shadow(-0.5px -0.5px 0px rgba(255,255,255,0.4))';
                            opacityStyle = 1;
                          }

                          // Use processed logo for Laser, original for others
                          const displayLogoUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : logoPreviewUrl;

                          // Add embroidery stitch overlay class
                          const embroideryClass = isEmbroidery ? 'embroidery-stitch' : '';

                          return (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${leftPercent}%`,
                                top: `${topPercent}%`,
                                transform: 'translate(-50%, -50%)',
                                width: `${widthPercent}%`,
                                height: `${heightPercent}%`,
                                zIndex: 10,
                                overflow: 'visible',
                                cursor: isDraggingBasicLogo ? 'grabbing' : 'grab',
                              }}
                              onMouseDown={handleBasicLogoDragStart}
                            >
                              <img
                                src={displayLogoUrl}
                                alt="Logo preview"
                                className={`basic-logo-overlay ${embroideryClass}`}
                                draggable={false}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  filter: filterStyle,
                                  opacity: opacityStyle,
                                  pointerEvents: 'none',
                                }}
                              />
                              {isEmbroidery && <div className="embroidery-texture-overlay" />}
                            </div>
                          );
                        })()
                      )}
                      {/* Print area indicator */}
                      {currentTechnique && product.svgWidth && product.svgHeight && (
                        (() => {
                          // Get image bounds within SVG viewBox
                          const imgX = product.svgImageX || 0;
                          const imgY = product.svgImageY || 0;
                          const imgW = product.svgImageWidth || product.svgWidth;
                          const imgH = product.svgImageHeight || product.svgHeight;

                          const centerX = currentTechnique.centroidX || product.svgWidth / 2;
                          const centerY = currentTechnique.centroidY || product.svgHeight / 2;
                          // Convert to image-relative percentage
                          const leftPercent = (centerX / product.svgWidth) * 100;
                          const topPercent = (centerY / product.svgHeight) * 100;
                          // Apply scale compensation for API coordinate mismatch
                          const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
                          const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
                          const widthPercent = (printAreaWidthPx / product.svgWidth) * 100;
                          const heightPercent = (printAreaHeightPx / product.svgHeight) * 100;

                          return (
                            <div
                              className="basic-print-area-indicator"
                              style={{
                                position: 'absolute',
                                left: `${leftPercent}%`,
                                top: `${topPercent}%`,
                                transform: 'translate(-50%, -50%)',
                                width: `${widthPercent}%`,
                                height: `${heightPercent}%`,
                                border: '2px dashed rgba(0, 43, 81, 0.5)',
                                borderRadius: '4px',
                                pointerEvents: 'none',
                              }}
                            />
                          );
                        })()
                      )}
                    </div>
                    {/* Upload placeholder if no logo */}
                    {!logoPreviewUrl && (
                      <div className="basic-upload-hint" onClick={() => fileInputRef.current?.click()}>
                        <i className="fas fa-cloud-upload-alt"></i>
                        <span>{t('clickOrDragToUpload')}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="basic-no-product">
                    <i className="fas fa-box-open"></i>
                    <div>Loading product...</div>
                  </div>
                )}
              </div>
            )}

            {/* Advanced Mode - Canvas Container */}
            {editorMode === 'advanced' && (
            <div className="editor-wrapper" ref={containerRef}>
              {isLoading && (
                <div className="loading-spinner">
                  <i className="fas fa-cog fa-spin"></i>
                  <i className="fas fa-cog fa-spin-reverse"></i>
                  <i className="fas fa-cog fa-spin"></i>
                </div>
              )}
              <div className="canvas-container">
                <div className="canvas-wrapper" ref={canvasWrapperRef} style={{ backgroundColor: canvasBackground }}>
                  <canvas ref={canvasRef} />
                </div>
                {!hasLogo && !isLoading && (
                  <div className="canvas-placeholder" onClick={() => fileInputRef.current?.click()}>
                    <i className="fas fa-cloud-upload-alt"></i>
                    <div>{t('clickOrDragToUpload')}</div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Right: Config Panel (40%) */}
          <div className="side-panel">
            <div className="config-header">
              <h3>{t('configureProduct')}</h3>
            </div>

            {/* Technique Selection */}
            <div className="config-section">
              <div className="config-section-title">
                <span className="step-number">1</span>
                {t('selectPrintTechnique')}
              </div>
              <div className="technique-options">
                {techniques.map((tech, index) => (
                  <div
                    key={`${tech.code}-${index}`}
                    className={`technique-option ${selectedTechnique === tech.code ? 'selected' : ''}`}
                    onClick={() => setSelectedTechnique(tech.code)}
                  >
                    <div className="technique-info">
                      <div className="technique-name">{tech.code}</div>
                      <div className="technique-details">
                        <i className="fas fa-circle" style={{ fontSize: 8 }}></i> {tech.dimensions}
                        <i className="fas fa-circle" style={{ fontSize: 8, marginLeft: 8 }}></i> {tech.maxColors === 1 ? `${t('oneColor')} (${t('engraving')})` : t('maxColors').replace('{count}', String(tech.maxColors))}
                      </div>
                    </div>
                    <span className="technique-badge">{tech.code}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div className="config-section">
              <div className="config-section-title">
                <span className="step-number">2</span>
                {t('numberOfColors')}
              </div>
              <div className="color-options">
                {colorOptions.map((color) => (
                  <div
                    key={color.id}
                    className={`color-option ${selectedColors === color.id ? 'selected' : ''}`}
                    onClick={() => setSelectedColors(color.id)}
                  >
                    {color.name}
                  </div>
                ))}
              </div>
            </div>

            {/* DEBUG: Always show variant count */}
            <div style={{ padding: '8px', background: '#ffeb3b', color: '#000', fontSize: '12px', marginBottom: '8px' }}>
              DEBUG: variants = {product?.variants?.length ?? 'undefined'}, product.sku = {product?.sku ?? 'undefined'}
            </div>

            {/* Variant/Color Selection */}
            {product?.variants && product.variants.length > 0 && (
              <div className="config-section">
                <div className="config-section-title">
                  <span className="step-number">3</span>
                  {t('colorVariants')}
                </div>
                <div className="variant-options">
                  {product.variants.map((variant) => (
                    <div
                      key={variant.variantCode}
                      className={`variant-option ${product.sku === variant.variantCode ? 'selected' : ''}`}
                      onClick={() => onVariantChange?.(variant.variantCode)}
                      title={variant.colorName}
                    >
                      <div
                        className="variant-color-circle"
                        style={{ backgroundColor: variant.hexCode }}
                      />
                      <span className="variant-name">{variant.colorName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logo Size Slider - Only in Basic Mode with logo uploaded */}
            {editorMode === 'basic' && basicLogoUrl && (
              <div className="config-section">
                <div className="config-section-title">
                  <span className="step-number">{product?.variants && product.variants.length > 0 ? '4' : '3'}</span>
                  {t('logoSize')}
                </div>
                <div className="basic-sliders">
                  <div className="slider-group">
                    <div className="slider-label">
                      <span>{t('logoSize')}</span>
                      <span className="slider-value">{basicLogoSize}%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={basicLogoSize}
                      onChange={(e) => handleBasicSizeChange(parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="action-btn" onClick={onClose}>
            <i className="fas fa-arrow-left"></i> {t('back')}
          </button>

          {/* Clipboard buttons - shown in both modes */}
          <button
            className={`action-btn ${copyFeedback === 'logo' ? 'success' : ''}`}
            onClick={handleCopyLogo}
            disabled={!hasLogo}
          >
            <i className={`fas ${copyFeedback === 'logo' ? 'fa-check' : 'fa-image'}`}></i>
            {copyFeedback === 'logo' ? t('copied') : t('copyLogo')}
          </button>
          <button
            className={`action-btn ${copyFeedback === 'svg' ? 'success' : ''}`}
            onClick={handleCopySvg}
            disabled={!hasLogo}
          >
            <i className={`fas ${copyFeedback === 'svg' ? 'fa-check' : 'fa-code'}`}></i>
            {copyFeedback === 'svg' ? t('copied') : t('copySvg')}
          </button>
          <button
            className={`action-btn ${copyFeedback === 'pdf' ? 'success' : ''}`}
            onClick={handleDownloadPdf}
            disabled={!hasLogo || isGeneratingPdf}
          >
            <i className={`fas ${isGeneratingPdf ? 'fa-spinner fa-spin' : copyFeedback === 'pdf' ? 'fa-check' : 'fa-file-pdf'}`}></i>
            {isGeneratingPdf ? t('generatingPdf') : copyFeedback === 'pdf' ? t('copied') : t('downloadPdf')}
          </button>

          {/* In Basic mode, show Save button directly */}
          {editorMode === 'basic' && (
            <button className="action-btn success" onClick={handleSave}>
              <i className="fas fa-check"></i> {t('save')}
            </button>
          )}

          {/* Spacer to push Magic Logo button to the right */}
          <div style={{ flex: 1 }}></div>

          {/* 2D Fullscreen button - visible in Basic mode */}
          {editorMode === 'basic' && logoPreviewUrl && (
            <button className="action-btn" onClick={() => setFullscreen2D(true)}>
              <i className="fas fa-expand"></i> 2D
            </button>
          )}

          {/* Magic 3D button - visible in Basic mode OR when Magic 2D slide is open */}
          {product?.has3DModel && (editorMode === 'basic' || slideOpen) && (
            <button className="action-btn primary" onClick={() => {
              if (logoPreviewUrl || basicLogoUrl) {
                localStorage.setItem('magic_logo_preview', logoPreviewUrl || basicLogoUrl || '');
                localStorage.setItem('magic_logo_technique', selectedTechnique);
              }
              setSlide3DOpen(true);
            }}>
              <i className="fas fa-cube"></i> Magic 3D
            </button>
          )}

          {/* Add to Order button - next to Magic 3D */}
          {mode !== 'configure' && (
          <button
            className={`action-btn ${addToOrderStatus === 'success' ? 'success' : addToOrderStatus === 'error' ? 'danger' : 'primary'}`}
            onClick={handleAddToOrder}
            disabled={addToOrderStatus === 'loading' || (!hasLogo && !basicLogoUrl)}
            style={{ marginLeft: '8px' }}
          >
            <i className={`fas ${
              addToOrderStatus === 'loading' ? 'fa-spinner fa-spin' :
              addToOrderStatus === 'success' ? 'fa-check' :
              addToOrderStatus === 'error' ? 'fa-exclamation-triangle' :
              'fa-shopping-cart'
            }`}></i>
            {addToOrderStatus === 'loading' ? t('addingToOrder') :
             addToOrderStatus === 'success' ? t('addedToOrder') :
             addToOrderStatus === 'error' ? t('addToOrderError') :
             t('addToOrder')}
          </button>
          )}

          {/* Only show Magic Logo slide button in Advanced mode - positioned right */}
          {editorMode === 'advanced' && (
            <button className="action-btn primary" onClick={() => setSlideOpen(true)}>
              <i className="fas fa-pencil-alt"></i> Magic Logo
            </button>
          )}
        </div>

        {/* Slide Panel - Magic Logo */}
        <div className={`slide-panel ${slideOpen ? 'open' : ''}`}>
          <div className="slide-panel-header">
            <h3>Magic Logo</h3>
            <button className="close-btn" onClick={() => setSlideOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="slide-panel-content">
            {/* Technique tabs */}
            <div className="technique-tabs">
              <div className="tab-label">{t('technique').toUpperCase()}</div>
              {techniques.map((tech, index) => (
                <div
                  key={`${tech.code}-${index}`}
                  className={`technique-tab ${selectedTechnique === tech.code ? 'selected' : ''}`}
                  onClick={() => setSelectedTechnique(tech.code)}
                >
                  {tech.code}
                </div>
              ))}
            </div>

            {/* Color variant selector */}
            {product?.variants && product.variants.length > 0 && (
              <div className="color-selector">
                {product.variants.map((variant) => (
                  <button
                    key={variant.variantCode}
                    className={`color-btn ${product.sku === variant.variantCode ? 'active' : ''}`}
                    title={variant.colorName}
                    onClick={() => onVariantChange?.(variant.variantCode)}
                    style={{ backgroundColor: variant.hexCode }}
                  />
                ))}
              </div>
            )}

            {/* Product Preview with Logo Overlay */}
            <div className="product-preview" ref={slidePreviewRef} style={{ overflow: 'hidden', cursor: 'grab' }}>
              {product?.imageUrl ? (
                <div className="preview-container" style={{ transform: `translate(${slidePreviewTransform.x}px, ${slidePreviewTransform.y}px) scale(${slidePreviewTransform.scale})`, transformOrigin: 'center center', transition: 'transform 0.1s ease-out' }}>
                  <div className="preview-image-wrapper">
                    <img src={product.imageUrl} alt={product.name} className={`product-image ${product.sku?.startsWith('376') ? 'product-376' : ''}`} />
                    {/* Logo overlay - positioned based on relative position within print area */}
                    {logoPreviewUrl && currentTechnique && product.svgWidth && product.svgHeight && (
                      (() => {
                        // Get image bounds within SVG viewBox (product image offset and size)
                        const imgX = product.svgImageX || 0;
                        const imgY = product.svgImageY || 0;
                        const imgW = product.svgImageWidth || product.svgWidth;
                        const imgH = product.svgImageHeight || product.svgHeight;

                        // Print area center (centroid) in viewBox pixels
                        const printCenterX = currentTechnique.centroidX || product.svgWidth / 2;
                        const printCenterY = currentTechnique.centroidY || product.svgHeight / 2;

                        // Print area dimensions in viewBox pixels (apply scale compensation for API coordinate mismatch)
                        const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
                        const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;

                        // Calculate print area top-left corner in viewBox coords
                        const printLeftPx = printCenterX - printAreaWidthPx / 2;
                        const printTopPx = printCenterY - printAreaHeightPx / 2;

                        // Use relative position from editor to calculate logo position in viewBox coords
                        const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
                        const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);

                        // Convert viewBox coords to image-relative percentage
                        // Subtract image offset, then divide by image size
                        const leftPercent = (logoCenterXPx / product.svgWidth) * 100;
                        const topPercent = (logoCenterYPx / product.svgHeight) * 100;

                        // Calculate logo size relative to image bounds
                        const logoWidthPx = logoRelativePos.scaleX * printAreaWidthPx;
                        const logoHeightPx = logoRelativePos.scaleY * printAreaHeightPx;
                        const widthPercent = (logoWidthPx / product.svgWidth) * 100;
                        const heightPercent = (logoHeightPx / product.svgHeight) * 100;

                        // Check technique type for different effects
                        const isEmboss = currentTechnique?.code?.startsWith('P');
                        const isLaser = currentTechnique?.code?.startsWith('L');
                        const isEmbroidery = currentTechnique?.code === 'E' || currentTechnique?.name?.toLowerCase().includes('bordur') || currentTechnique?.name?.toLowerCase().includes('embroid');

                        // Determine filter based on technique
                        let filterStyle = 'none';
                        let opacityStyle = 1;

                        if (isEmboss) {
                          // Emboss/Preging: dark gray/black with 3D relief effect
                          filterStyle = 'grayscale(100%) brightness(0.3) contrast(1.3) drop-shadow(2px 2px 1px rgba(0,0,0,0.7)) drop-shadow(-1px -1px 1px rgba(255,255,255,0.3))';
                          opacityStyle = 0.95;
                        } else if (isLaser) {
                          // Laser: use vectorized gray version, no extra filter needed
                          filterStyle = 'none';
                          opacityStyle = 1;
                        } else if (isEmbroidery) {
                          // Embroidery/Borduren: stronger stitch effect with saturated colors and texture
                          filterStyle = 'saturate(1.3) contrast(1.2) brightness(1.05) drop-shadow(1px 1px 0px rgba(0,0,0,0.5)) drop-shadow(-0.5px -0.5px 0px rgba(255,255,255,0.4))';
                          opacityStyle = 1;
                        }

                        // Use processed logo for Laser, original for others
                        const displayLogoUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : logoPreviewUrl;

                        return (
                          <div
                            style={{
                              position: 'absolute',
                              left: `${leftPercent}%`,
                              top: `${topPercent}%`,
                              transform: 'translate(-50%, -50%)',
                              width: `${widthPercent}%`,
                              height: `${heightPercent}%`,
                              zIndex: 10,
                            }}
                          >
                            <img
                              src={displayLogoUrl}
                              alt="Logo preview"
                              className={`logo-overlay ${isEmbroidery ? 'embroidery-stitch' : ''}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                filter: filterStyle,
                                opacity: opacityStyle,
                              }}
                            />
                            {isEmbroidery && <div className="embroidery-texture-overlay" />}
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              ) : (
                <div className="preview-placeholder">
                  <i className="fas fa-image" style={{ fontSize: 48, color: '#ccc' }}></i>
                  <div>{t('productPreview')}</div>
                </div>
              )}
            </div>
          </div>

          <div className="slide-panel-footer">
            <button className="action-btn" onClick={() => setSlideOpen(false)}>
              <i className="fas fa-arrow-left"></i> {t('back')}
            </button>
            <button className="action-btn" onClick={() => setFullscreen2D(true)}>
              <i className="fas fa-expand"></i> Fullscreen
            </button>
            {product?.has3DModel && (
              <button className="action-btn primary" onClick={() => {
                // Store logo in localStorage for 3D viewer to pick up
                if (logoPreviewUrl) {
                  localStorage.setItem('magic_logo_preview', logoPreviewUrl);
                  localStorage.setItem('magic_logo_technique', selectedTechnique);
                }
                // Open 3D slide panel instead of new tab
                setSlide3DOpen(true);
              }}>
                <i className="fas fa-cube"></i> Magic 3D
              </button>
            )}
            {/* Add to Order button in slide panel footer */}
            {mode !== 'configure' && (
            <button
              className={`action-btn ${addToOrderStatus === 'success' ? 'success' : addToOrderStatus === 'error' ? 'danger' : 'primary'}`}
              onClick={handleAddToOrder}
              disabled={addToOrderStatus === 'loading' || !logoPreviewUrl}
              style={{ marginLeft: '8px' }}
            >
              <i className={`fas ${
                addToOrderStatus === 'loading' ? 'fa-spinner fa-spin' :
                addToOrderStatus === 'success' ? 'fa-check' :
                addToOrderStatus === 'error' ? 'fa-exclamation-triangle' :
                'fa-shopping-cart'
              }`}></i>
              {addToOrderStatus === 'loading' ? t('addingToOrder') :
               addToOrderStatus === 'success' ? t('addedToOrder') :
               addToOrderStatus === 'error' ? t('addToOrderError') :
               t('addToOrder')}
            </button>
            )}
          </div>
        </div>

        {/* Magic 3D Slide Panel - slides over Magic Logo panel */}
        <div className={`slide-panel slide-panel-3d ${slide3DOpen ? 'open' : ''}`}>
          <div className="slide-panel-header">
            <h3><i className="fas fa-cube"></i> {t('magic3dPreview')}</h3>
            <button className="close-btn" onClick={() => setSlide3DOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="slide-panel-content slide-3d-content">
            {/* Technique tabs for 3D panel */}
            <div className="technique-tabs technique-tabs-3d">
              <div className="tab-label">{t('technique').toUpperCase()}</div>
              {techniques.map((tech, index) => (
                <div
                  key={`3d-${tech.code}-${index}`}
                  className={`technique-tab ${selectedTechnique === tech.code ? 'selected' : ''}`}
                  onClick={() => setSelectedTechnique(tech.code)}
                >
                  {tech.code}
                </div>
              ))}
            </div>

            {/* Always render iframe when product has 3D model, hide when slide is closed */}
            {product?.has3DModel && (
              <iframe
                key={product.sku}
                ref={iframe3DRef}
                onLoad={() => {
                  // Send initial logo to iframe when it loads
                  if (logoPreviewUrl) {
                    // Send after delays to ensure 3D viewer is ready
                    setTimeout(() => sendLogoTo3DViewer(logoPreviewUrl), 500);
                    setTimeout(() => sendLogoTo3DViewer(logoPreviewUrl), 1500);
                    setTimeout(() => sendLogoTo3DViewer(logoPreviewUrl), 3000);
                  }
                }}
                src={`${get3DViewerUrl()}/?sku=${product.sku}&technique=${initialTechniqueRef.current}&embed=true`}
                title="Magic 3D Viewer"
                className="magic-3d-iframe"
                style={{ display: slide3DOpen ? 'block' : 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              />
            )}
          </div>

          <div className="slide-panel-footer">
            <button className="action-btn" onClick={() => setSlide3DOpen(false)}>
              <i className="fas fa-arrow-left"></i> {t('backTo2D')}
            </button>
            <button
              className={`action-btn copy-btn ${copyFeedback === '3d' ? 'copied' : ''}`}
              onClick={handleCopy3D}
              disabled={isCopying3D}
            >
              <i className={`fas ${isCopying3D ? 'fa-spinner fa-spin' : copyFeedback === '3d' ? 'fa-check' : 'fa-copy'}`}></i>
              {isCopying3D ? t('copying3D') : copyFeedback === '3d' ? t('copied') : t('copy3D')}
            </button>
            <button
              className="action-btn ai-render-btn"
              onClick={handleAiRenderOpen}
              disabled={aiRenderStep === 'generating'}
            >
              <i className={`fas ${aiRenderStep === 'generating' ? 'fa-spinner fa-spin' : 'fa-robot'}`}></i> {t('aiRender')}
            </button>
            <button className="action-btn" onClick={() => setFullscreen3D(true)}>
              <i className="fas fa-expand"></i> Fullscreen
            </button>
          </div>
        </div>
      </div>

      {/* AI Render Overlay */}
      {showAiRenderOverlay && (
        <div className="ai-render-overlay" onClick={handleAiRenderClose}>
          <div className="ai-render-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-render-header">
              <span className="ai-render-title">✨ AI Render - Magic e-VERSE AI</span>
              <button className="ai-render-close-btn" onClick={handleAiRenderClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Option Tabs */}
            <div className="ai-render-tabs">
              <button
                className={`ai-render-tab ${aiRenderOption === 1 ? 'active' : ''}`}
                onClick={() => { if (aiRenderStep !== 'generating') { setAiRenderOption(1); setAiRenderStep('ready'); setAiRenderResult(null); setAiRenderError(null); } }}
                disabled={aiRenderStep === 'generating'}
              >
                <i className="fas fa-wand-magic-sparkles"></i>
                <span>Option 1</span>
                <small>Mood Shot</small>
              </button>
              <button
                className={`ai-render-tab ${aiRenderOption === 2 ? 'active' : ''}`}
                onClick={() => { if (aiRenderStep !== 'generating') { setAiRenderOption(2); setAiRenderStep('ready'); setAiRenderResult(null); setAiRenderError(null); setAiVideoPhase('ready'); setAiVideoMoodShot(null); setAiVideoResult(null); setAiVideoError(null); } }}
                disabled={aiRenderStep === 'generating'}
              >
                <i className="fas fa-video"></i>
                <span>Option 2</span>
                <small>Video</small>
              </button>
            </div>

            <div className="ai-render-body">
              {/* Option 5: Video generation layout */}
              {aiRenderOption === 2 ? (
                <>
                  {/* Two-step pipeline display */}
                  <div className="ai-render-video-pipeline">
                    {/* Step 1: Mood Shot */}
                    <div className={`ai-render-video-step ${aiVideoPhase === 'generating-image' ? 'active' : ''} ${aiVideoPhase !== 'ready' && aiVideoPhase !== 'generating-image' ? 'done' : ''}`}>
                      <div className="ai-render-video-step-header">
                        <span className="ai-render-video-step-number">1</span>
                        <span className="ai-render-video-step-title">Mood Shot</span>
                        {aiVideoPhase === 'generating-image' && <i className="fas fa-spinner fa-spin"></i>}
                        {(aiVideoPhase === 'generating-video' || aiVideoPhase === 'success' || aiVideoPhase === 'image-done') && <i className="fas fa-check" style={{ color: '#4CAF50' }}></i>}
                        {aiVideoPhase === 'error' && !aiVideoMoodShot && <i className="fas fa-times" style={{ color: '#f44336' }}></i>}
                      </div>
                      <div className="ai-render-video-step-content">
                        {aiVideoPhase === 'ready' && (
                          <div className="ai-render-placeholder">
                            <i className="fas fa-image"></i>
                            <span>nano-banana-2</span>
                          </div>
                        )}
                        {aiVideoPhase === 'generating-image' && (
                          <div className="ai-render-placeholder generating">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Generating mood shot...</span>
                          </div>
                        )}
                        {aiVideoMoodShot && (
                          <img src={aiVideoMoodShot} alt="Mood Shot" />
                        )}
                        {aiVideoPhase === 'error' && !aiVideoMoodShot && (
                          <div className="ai-render-placeholder error">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>{aiVideoError || 'Failed'}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="ai-render-video-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>

                    {/* Step 2: Video */}
                    <div className={`ai-render-video-step ${aiVideoPhase === 'generating-video' ? 'active' : ''} ${aiVideoPhase === 'success' ? 'done' : ''}`}>
                      <div className="ai-render-video-step-header">
                        <span className="ai-render-video-step-number">2</span>
                        <span className="ai-render-video-step-title">Video</span>
                        {aiVideoPhase === 'generating-video' && <i className="fas fa-spinner fa-spin"></i>}
                        {aiVideoPhase === 'success' && <i className="fas fa-check" style={{ color: '#4CAF50' }}></i>}
                        {aiVideoPhase === 'error' && aiVideoMoodShot && <i className="fas fa-times" style={{ color: '#f44336' }}></i>}
                      </div>
                      <div className="ai-render-video-step-content">
                        {(aiVideoPhase === 'ready' || aiVideoPhase === 'generating-image') && (
                          <div className="ai-render-placeholder">
                            <i className="fas fa-video"></i>
                            <span>grok-imagine</span>
                          </div>
                        )}
                        {aiVideoPhase === 'generating-video' && (
                          <div className="ai-render-placeholder generating">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Generating video...</span>
                          </div>
                        )}
                        {aiVideoPhase === 'success' && aiVideoResult && (
                          <video
                            src={aiVideoResult}
                            controls
                            autoPlay
                            loop
                            muted
                            style={{ width: '100%', borderRadius: '8px' }}
                          />
                        )}
                        {aiVideoPhase === 'error' && aiVideoMoodShot && (
                          <div className="ai-render-placeholder error">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>{aiVideoError || 'Failed'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Video settings - visible in ready state */}
                  {aiVideoPhase === 'ready' && (
                    <>
                      <div className="ai-render-aspect-ratio">
                        <label className="ai-render-prompt-label">Duration:</label>
                        <div className="ai-render-aspect-options">
                          <button
                            className={`ai-render-aspect-btn ${aiVideoDuration === '6' ? 'active' : ''}`}
                            onClick={() => setAiVideoDuration('6')}
                          >
                            6 seconds
                          </button>
                          <button
                            className={`ai-render-aspect-btn ${aiVideoDuration === '10' ? 'active' : ''}`}
                            onClick={() => setAiVideoDuration('10')}
                          >
                            10 seconds
                          </button>
                        </div>
                      </div>
                      <div className="ai-render-aspect-ratio">
                        <label className="ai-render-prompt-label">Resolution:</label>
                        <div className="ai-render-aspect-options">
                          <button
                            className={`ai-render-aspect-btn ${aiVideoResolution === '480p' ? 'active' : ''}`}
                            onClick={() => setAiVideoResolution('480p')}
                          >
                            480p
                          </button>
                          <button
                            className={`ai-render-aspect-btn ${aiVideoResolution === '720p' ? 'active' : ''}`}
                            onClick={() => setAiVideoResolution('720p')}
                          >
                            720p
                          </button>
                        </div>
                      </div>
                      <div className="ai-render-prompt-area">
                        <label className="ai-render-prompt-label">Video Motion Prompt:</label>
                        <textarea
                          className="ai-render-prompt-input"
                          value={aiVideoPrompt}
                          onChange={(e) => setAiVideoPrompt(e.target.value)}
                          rows={2}
                          placeholder="Describe the camera motion and animation..."
                        />
                      </div>
                      <div className="ai-render-prompt-area">
                        <label className="ai-render-prompt-label">{t('aiRenderPromptLabel')}</label>
                        <textarea
                          className="ai-render-prompt-input"
                          value={aiRenderPrompt}
                          onChange={(e) => setAiRenderPrompt(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </>
                  )}

                  {/* Progress bar - visible during generation */}
                  {aiRenderStep === 'generating' && (
                    <div className="ai-render-progress-area">
                      <div className="ai-render-progress-bar">
                        <div
                          className="ai-render-progress-fill"
                          style={{ width: `${aiRenderProgress}%` }}
                        />
                      </div>
                      <div className="ai-render-progress-text">
                        ⏳ {aiVideoPhase === 'generating-image' ? 'Step 1: Generating mood shot' : 'Step 2: Generating video'} ({aiRenderElapsed}s)
                      </div>
                    </div>
                  )}

                  {/* Success info */}
                  {aiVideoPhase === 'success' && (
                    <div className="ai-render-success-info">
                      ✅ Video generated successfully ({aiRenderElapsed}s)
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Option 1: Single model layout */}
                  <div className="ai-render-images">
                    {aiRenderOriginalImage && (
                      <div className="ai-render-image-box">
                        <span className="ai-render-image-label">Original Photo</span>
                        <img src={aiRenderOriginalImage} alt="Original Product" />
                      </div>
                    )}
                    <div className="ai-render-image-box">
                      <span className="ai-render-image-label">3D + Logo</span>
                      {aiRenderScreenshot && (
                        <img src={aiRenderScreenshot} alt="3D Screenshot" />
                      )}
                    </div>
                    <div className="ai-render-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                    <div className="ai-render-image-box ai-render-result-box">
                      {aiRenderStep === 'ready' && (
                        <div className="ai-render-placeholder">
                          <i className="fas fa-image"></i>
                          <span>{t('aiRenderReady')}</span>
                        </div>
                      )}
                      {aiRenderStep === 'generating' && (
                        <div className="ai-render-placeholder generating">
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>{t('aiRendering')}</span>
                        </div>
                      )}
                      {aiRenderStep === 'success' && aiRenderResult && (
                        <img src={aiRenderResult} alt="AI Render Result" />
                      )}
                      {aiRenderStep === 'error' && (
                        <div className="ai-render-placeholder error">
                          <i className="fas fa-exclamation-triangle"></i>
                          <span>{aiRenderError || t('aiRenderError')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Prompt area - visible in ready state */}
                  {aiRenderStep === 'ready' && (
                    <div className="ai-render-prompt-area">
                      <label className="ai-render-prompt-label">{t('aiRenderPromptLabel')}</label>
                      <textarea
                        className="ai-render-prompt-input"
                        value={aiRenderPrompt}
                        onChange={(e) => setAiRenderPrompt(e.target.value)}
                        rows={3}
                      />
                    </div>
                  )}

                  {/* Prompt display - visible during generation */}
                  {aiRenderStep === 'generating' && (
                    <div className="ai-render-prompt-display">
                      <span className="ai-render-prompt-label">{t('aiRenderPromptLabel')}</span>
                      <span className="ai-render-prompt-text">{aiRenderPrompt.length > 80 ? aiRenderPrompt.substring(0, 80) + '...' : aiRenderPrompt}</span>
                    </div>
                  )}

                  {/* Progress bar - visible during generation */}
                  {aiRenderStep === 'generating' && (
                    <div className="ai-render-progress-area">
                      <div className="ai-render-progress-bar">
                        <div
                          className="ai-render-progress-fill"
                          style={{ width: `${aiRenderProgress}%` }}
                        />
                      </div>
                      <div className="ai-render-progress-text">
                        {aiRenderElapsed >= 45
                          ? `⏳ ${t('aiRenderTakingLonger')} (${aiRenderElapsed}s)`
                          : `⏳ ${t('aiRendering')} ${aiRenderElapsed}s / 45s`
                        }
                      </div>
                    </div>
                  )}

                  {/* Success info */}
                  {aiRenderStep === 'success' && (
                    <div className="ai-render-success-info">
                      ✅ {t('aiRenderSuccess')} ({aiRenderElapsed}s)
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="ai-render-footer">
              {aiRenderStep === 'ready' && (
                <button
                  className="ai-render-action-btn primary"
                  onClick={aiRenderOption === 2 ? handleAiRenderGenerateVideo : handleAiRenderGenerate}
                >
                  🚀 {aiRenderOption === 2 ? 'Generate Video' : t('aiRenderGenerate')}
                </button>
              )}
              {aiRenderStep === 'success' && aiRenderOption !== 2 && (
                <>
                  <button className="ai-render-action-btn" onClick={handleAiRenderCopy}>
                    <i className="fas fa-copy"></i> {t('aiRenderCopy')}
                  </button>
                  <button className="ai-render-action-btn" onClick={handleAiRenderDownload}>
                    <i className="fas fa-download"></i> {t('aiRenderDownload')}
                  </button>
                  <button className="ai-render-action-btn" onClick={() => {
                    setAiRenderStep('ready');
                    setAiRenderResult(null);
                    setAiRenderProgress(0);
                    setAiRenderElapsed(0);
                  }}>
                    <i className="fas fa-redo"></i> {t('aiRenderRetry')}
                  </button>
                </>
              )}
              {aiRenderStep === 'success' && aiRenderOption === 2 && (
                <>
                  {aiVideoResult && (
                    <button className="ai-render-action-btn" onClick={() => {
                      const link = document.createElement('a');
                      link.href = aiVideoResult;
                      link.download = `ai-video-${product?.name || 'product'}-${Date.now()}.mp4`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}>
                      <i className="fas fa-download"></i> Download Video
                    </button>
                  )}
                  <button className="ai-render-action-btn" onClick={() => {
                    setAiRenderStep('ready');
                    setAiRenderProgress(0);
                    setAiRenderElapsed(0);
                    setAiVideoPhase('ready');
                    setAiVideoMoodShot(null);
                    setAiVideoResult(null);
                    setAiVideoError(null);
                  }}>
                    <i className="fas fa-redo"></i> {t('aiRenderRetry')}
                  </button>
                </>
              )}
              {aiRenderStep === 'error' && (
                <button className="ai-render-action-btn" onClick={() => {
                  setAiRenderStep('ready');
                  setAiRenderError(null);
                  setAiRenderProgress(0);
                  setAiRenderElapsed(0);
                  if (aiRenderOption === 2) {
                    setAiVideoPhase('ready');
                    setAiVideoMoodShot(null);
                    setAiVideoResult(null);
                    setAiVideoError(null);
                  }
                }}>
                  <i className="fas fa-redo"></i> {t('aiRenderRetry')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen 2D Overlay */}
      {fullscreen2D && product?.imageUrl && (
        <div className="fullscreen-overlay" onClick={() => setFullscreen2D(false)}>
          <button className="fullscreen-close" onClick={() => setFullscreen2D(false)}>
            <i className="fas fa-times"></i>
          </button>
          <div className="fullscreen-content" ref={fullscreenContentRef} onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-image-wrapper" style={{ transform: `translate(${fullscreenTransform.x}px, ${fullscreenTransform.y}px) scale(${fullscreenTransform.scale})`, transition: 'transform 0.1s ease-out' }}>
              <img src={product.imageUrl} alt={product.name} className="fullscreen-product-image" />
              {/* Logo overlay (apply scale compensation for API coordinate mismatch) */}
              {logoPreviewUrl && currentTechnique && product.svgWidth && product.svgHeight && (
                (() => {
                  const printCenterX = currentTechnique.centroidX || product.svgWidth / 2;
                  const printCenterY = currentTechnique.centroidY || product.svgHeight / 2;
                  const printAreaWidthPx = (currentTechnique.printAreaWidth || 100) * PRINT_AREA_SCALE_COMPENSATION;
                  const printAreaHeightPx = (currentTechnique.printAreaHeight || 100) * PRINT_AREA_SCALE_COMPENSATION;
                  const printLeftPx = printCenterX - printAreaWidthPx / 2;
                  const printTopPx = printCenterY - printAreaHeightPx / 2;
                  const logoCenterXPx = printLeftPx + (logoRelativePos.x * printAreaWidthPx);
                  const logoCenterYPx = printTopPx + (logoRelativePos.y * printAreaHeightPx);
                  const leftPercent = (logoCenterXPx / product.svgWidth) * 100;
                  const topPercent = (logoCenterYPx / product.svgHeight) * 100;
                  const logoWidthPx = logoRelativePos.scaleX * printAreaWidthPx;
                  const logoHeightPx = logoRelativePos.scaleY * printAreaHeightPx;
                  const widthPercent = (logoWidthPx / product.svgWidth) * 100;
                  const heightPercent = (logoHeightPx / product.svgHeight) * 100;

                  const isEmboss = currentTechnique?.code?.startsWith('P');
                  const isLaser = currentTechnique?.code?.startsWith('L');
                  const isEmbroidery = currentTechnique?.code === 'E' || currentTechnique?.name?.toLowerCase().includes('bordur') || currentTechnique?.name?.toLowerCase().includes('embroid');

                  let filterStyle = 'none';
                  if (isEmboss) {
                    filterStyle = 'grayscale(100%) brightness(0.3) contrast(1.3) drop-shadow(2px 2px 1px rgba(0,0,0,0.7)) drop-shadow(-1px -1px 1px rgba(255,255,255,0.3))';
                  } else if (isEmbroidery) {
                    filterStyle = 'saturate(1.3) contrast(1.2) brightness(1.05) drop-shadow(1px 1px 0px rgba(0,0,0,0.5)) drop-shadow(-0.5px -0.5px 0px rgba(255,255,255,0.4))';
                  }

                  const displayLogoUrl = isLaser && laserProcessedLogoUrl ? laserProcessedLogoUrl : logoPreviewUrl;

                  return (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${leftPercent}%`,
                        top: `${topPercent}%`,
                        transform: 'translate(-50%, -50%)',
                        width: `${widthPercent}%`,
                        height: `${heightPercent}%`,
                        zIndex: 10,
                      }}
                    >
                      <img
                        src={displayLogoUrl}
                        alt="Logo"
                        className={isEmbroidery ? 'embroidery-stitch' : ''}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          filter: filterStyle,
                        }}
                      />
                      {isEmbroidery && <div className="embroidery-texture-overlay" />}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen 3D Overlay */}
      {fullscreen3D && product?.has3DModel && (
        <div className="fullscreen-overlay" onClick={() => setFullscreen3D(false)}>
          <button className="fullscreen-close" onClick={() => setFullscreen3D(false)}>
            <i className="fas fa-times"></i>
          </button>
          <div className="fullscreen-content fullscreen-3d" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`${get3DViewerUrl()}/?sku=${product.sku}&technique=${selectedTechnique}&embed=true`}
              title="Magic 3D Viewer Fullscreen"
              className="fullscreen-3d-iframe"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              onLoad={(e) => {
                // Send logo to fullscreen 3D viewer
                if (logoPreviewUrl) {
                  const iframe = e.target as HTMLIFrameElement;
                  const sendLogo = () => {
                    iframe.contentWindow?.postMessage({
                      type: 'UPDATE_LOGO',
                      logoUrl: logoPreviewUrl,
                      technique: selectedTechnique,
                      position: logoRelativePos
                    }, '*');
                  };
                  setTimeout(sendLogo, 500);
                  setTimeout(sendLogo, 1500);
                  setTimeout(sendLogo, 3000);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Logo Wizard */}
      {showWizard && wizardLogoBase64 && (
        <LogoWizard
          logoBase64={wizardLogoBase64}
          originalBase64={wizardOriginalBase64 || undefined}
          techniques={techniques}
          language={language}
          originalFormat={wizardOriginalFormat}
          onComplete={handleWizardComplete}
          onCancel={handleWizardCancel}
        />
      )}

      {/* Files Download Modal */}
      {showFilesModal && filesModalData && (
        <div className="files-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        }}>
          <div className="files-modal" style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: '#4CAF50',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px',
              }}>
                <i className="fas fa-check" style={{ color: '#fff', fontSize: '28px' }}></i>
              </div>
              <h2 style={{ margin: '0 0 10px', color: '#333' }}>
                {language === 'de' ? 'Bestellung hinzugefügt!' : language === 'nl' ? 'Bestelling toegevoegd!' : 'Order added!'}
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                Transactie: <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>{filesModalData.transactionId}</code>
              </p>
            </div>

            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px', marginBottom: '20px' }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>{filesModalData.productSku}</strong> • {filesModalData.techniqueName} • {filesModalData.quantity} {language === 'de' ? 'Stück' : 'stuks'}
              </div>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ fontSize: '14px', color: '#333', marginBottom: '12px' }}>
                <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
                {language === 'de' ? 'Dateien herunterladen' : language === 'nl' ? 'Bestanden downloaden' : 'Download files'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filesModalData.files.screenshot && (
                  <a
                    href={filesModalData.files.screenshot}
                    download={`preview_${filesModalData.transactionId}.jpg`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#e9ecef'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  >
                    <i className="fas fa-image" style={{ marginRight: '10px', color: '#4CAF50' }}></i>
                    <span style={{ flex: 1 }}>Preview (JPG)</span>
                    <i className="fas fa-external-link-alt" style={{ fontSize: '12px', color: '#999' }}></i>
                  </a>
                )}
                {filesModalData.files.screenshot_hq && (
                  <a
                    href={filesModalData.files.screenshot_hq}
                    download={`preview_hq_${filesModalData.transactionId}.jpg`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#e9ecef'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  >
                    <i className="fas fa-image" style={{ marginRight: '10px', color: '#2196F3' }}></i>
                    <span style={{ flex: 1 }}>Preview HQ (JPG)</span>
                    <i className="fas fa-external-link-alt" style={{ fontSize: '12px', color: '#999' }}></i>
                  </a>
                )}
                {filesModalData.files.logo_original && (
                  <a
                    href={filesModalData.files.logo_original}
                    download={`logo_${filesModalData.transactionId}.png`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#e9ecef'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  >
                    <i className="fas fa-file-image" style={{ marginRight: '10px', color: '#9C27B0' }}></i>
                    <span style={{ flex: 1 }}>Logo (PNG)</span>
                    <i className="fas fa-external-link-alt" style={{ fontSize: '12px', color: '#999' }}></i>
                  </a>
                )}
                {filesModalData.files.pdf && (
                  <a
                    href={filesModalData.files.pdf}
                    download={`korrekturabzug_${filesModalData.transactionId}.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#e9ecef'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  >
                    <i className="fas fa-file-pdf" style={{ marginRight: '10px', color: '#F44336' }}></i>
                    <span style={{ flex: 1 }}>Korrekturabzug (PDF)</span>
                    <i className="fas fa-external-link-alt" style={{ fontSize: '12px', color: '#999' }}></i>
                  </a>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setShowFilesModal(false);
                  setFilesModalData(null);
                  // If in iframe, close the modal
                  if (window.parent !== window) {
                    onClose();
                  }
                }}
                style={{
                  padding: '12px 30px',
                  background: '#002B51',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <i className="fas fa-check" style={{ marginRight: '8px' }}></i>
                {language === 'de' ? 'Schließen' : language === 'nl' ? 'Sluiten' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagicLogoModal;
